import React, { useState, useMemo, useCallback, useEffect, useReducer, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useToast } from '../components/Toast';
import { GoogleMap, DirectionsRenderer, Marker } from '@react-google-maps/api';
import { useGoogleMaps } from '../context/GoogleMapsContext';
import { collection, onSnapshot, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Calendar, Clock, MapPin, Users, CheckCircle, ChevronLeft, ChevronRight,
  Plus, Search, Phone, Navigation, DollarSign, FileText, Map, List,
  X, Zap, Home, Car, Bell, Camera, MessageSquare, Timer, Fuel,
  ChevronDown, ChevronUp, ExternalLink, AlertCircle, UserPlus, Shuffle,
  ArrowRight, Lightbulb, Check, AlertTriangle, Radio, Wifi, Trash2, Edit2,
  Columns, Send, Mail, Loader, RefreshCw, CalendarDays, MoreHorizontal, Printer, Sparkles
} from 'lucide-react';
import { format, addDays, subDays, parseISO, formatDistanceToNow } from 'date-fns';
import {
  OFFICE, jobStatuses, serviceTypes, formatJobTime, formatDuration,
  groupJobsByTeam, calculateRouteStats, getGoogleMapsRouteUrl, getGoogleMapsDirectionsUrl,
  REVENUE_GOAL_PER_STAFF, HOURS_PER_STAFF_DAY, REVENUE_GOAL_PER_HOUR
} from '../utils/jobData';
import RouteBuilder from '../components/RouteBuilder';
import RouteMapView from '../components/RouteMapView';
import DispatchDateNav from '../components/DispatchDateNav';
import DispatchTopBar from '../components/DispatchTopBar';
import DispatchStatStrip from '../components/DispatchStatStrip';
import DispatchGoalPanel from '../components/DispatchGoalPanel';
import DispatchAttentionBar from '../components/DispatchAttentionBar';
import RecurringJobModal from '../components/RecurringJobModal';
import ShiftScheduler from '../components/ShiftScheduler';
import TimeOffManager from '../components/TimeOffManager';
import DispatchLog from '../components/DispatchLog';
import DispatchPrintView from '../components/DispatchPrintView';
import DayNotesPanel from '../components/DayNotesPanel';
import AutoGroupModal from '../components/AutoGroupModal';
import '../components/DispatchLog.css';
import '../components/TimeOffManager.css';
import { sendAppointmentReminder, sendWelcomeEmail, formatTimeForEmail } from '../utils/emailApi';
import { sendSMS, notifyAppointmentConfirmation } from '../utils/notificationsApi';
import { postJobAssigned, postTeamRoute } from '../utils/slackApi';
import '../components/RouteBuilder.css';
import '../components/RouteMapView.css';
import '../components/ShiftScheduler.css';
import './Dispatch.css';

const mapContainerStyle = { width: '100%', height: '100%' };
const defaultCenter = { lat: 38.0293, lng: -78.4767 };
const mapOptions = { disableDefaultUI: false, zoomControl: true, streetViewControl: false, mapTypeControl: false, fullscreenControl: true };
const teamColors = ['#2c5f4e', '#b8926a', '#2563eb', '#9333ea', '#dc2626', '#ea580c', '#0891b2', '#65a30d'];
const routeColors = ['#2c5f4e', '#dc2626', '#2563eb', '#9333ea', '#ea580c'];

// Modal state reducer to consolidate 30+ modal-related useState calls
const modalInitialState = {
  showAssignModal: false,
  jobToAssign: null,
  selectedTeamMembers: [],
  showRescheduleModal: false,
  rescheduleData: { date: '', scheduledTime: '09:00', duration: 2.5 },
  showAddJobModal: false,
  showRecurringModal: false,
  editingJobForRecurring: null,
  showShiftScheduler: false,
  showDuplicateWarning: false,
  duplicateClients: [],
  pendingJobData: null,
  sendingReminder: null,
  showReminderModal: null,
  showTimeOffManager: false,
  showRunningBehindModal: null,
  runningBehindDelay: '15',
  runningBehindCustom: '',
  runningBehindNote: '',
  sendingRunningBehind: null,
  showConfirmationPrompt: false,
  confirmationJobData: null,
  sendingConfirmation: false,
  editingJobField: null,
  editJobNotes: '',
  editJobType: '',
  editJobPrice: '',
  showSeriesModal: null,
  savingJobEdit: false,
  showNotifyClientModal: null,
  notifyType: 'booked',
  sendingNotify: false,
  showBatchReminderModal: false,
  batchSelectedJobs: {},
  batchSending: false,
  batchProgress: { sent: 0, failed: 0, skipped: 0, total: 0 },
  batchResults: null,
  showMoreActions: false,
};

function modalReducer(state, action) {
  switch (action.type) {
    case 'SET':
      return { ...state, ...action.payload };
    case 'RESET_MODAL':
      // Reset a specific modal and its related states
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

function Dispatch() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { state, updateJob, addJob, deleteJob, deleteSeriesJobs, createInvoiceForJob, addClient, findDuplicateClients, addProperty, logClientCommunication, getTimeOffRequests, hasTimeOffOnDate, getEmployeeById, logDispatchActivity } = useApp();

  // Core view state
  const [view, setView] = useState('map');
  const [selectedDate, setSelectedDate] = useState(location.state?.selectedDate || format(new Date(), 'yyyy-MM-dd'));
  const [selectedJob, setSelectedJob] = useState(null);
  const [editingPrice, setEditingPrice] = useState(null);
  const [showDispatchLog, setShowDispatchLog] = useState(false);
  const [selectedTeamFilter, setSelectedTeamFilter] = useState(null);
  const [showTeamFilter, setShowTeamFilter] = useState(false);
  const [showOpenSlots, setShowOpenSlots] = useState(true);
  const [expandedRoute, setExpandedRoute] = useState(null);
  const [directions, setDirections] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showOptimizePanel, setShowOptimizePanel] = useState(false);
  const [optimizationSuggestions, setOptimizationSuggestions] = useState([]);
  const [liveLocations, setLiveLocations] = useState({});
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [timeRecords, setTimeRecords] = useState([]);
  const [showAutoGroupModal, setShowAutoGroupModal] = useState(false);
  const [isSendingRoutes, setIsSendingRoutes] = useState(false);
  // Ticks every 60s so live clocked-in hours in the Daily Goal Tracker climb through the day.
  const [now, setNow] = useState(() => Date.now());

  // Stat-chip status filter (null | 'unassigned' | 'in_progress' | 'completed')
  const [statusFilter, setStatusFilter] = useState(null);
  // UI density (persisted)
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('dispatch.density') === 'compact' ? 'compact' : 'comfortable'; }
    catch { return 'comfortable'; }
  });
  const toggleDensity = useCallback(() => {
    setDensity(prev => {
      const next = prev === 'compact' ? 'comfortable' : 'compact';
      try { localStorage.setItem('dispatch.density', next); } catch (e) { /* noop */ }
      return next;
    });
  }, []);
  const attentionBarRef = useRef(null);
  const scrollToAttention = useCallback(() => {
    attentionBarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Consolidated modal state via useReducer (replaces 30+ useState calls)
  const [modals, dispatch] = useReducer(modalReducer, modalInitialState);
  const setModal = useCallback((payload) => dispatch({ type: 'SET', payload }), []);

  // Destructure for easy access (maintains compatibility with existing code)
  const {
    showAssignModal, jobToAssign, selectedTeamMembers,
    showRescheduleModal, rescheduleData,
    showAddJobModal, showRecurringModal, editingJobForRecurring,
    showShiftScheduler,
    showDuplicateWarning, duplicateClients, pendingJobData,
    sendingReminder, showReminderModal, showTimeOffManager,
    showRunningBehindModal, runningBehindDelay, runningBehindCustom,
    runningBehindNote, sendingRunningBehind,
    showConfirmationPrompt, confirmationJobData, sendingConfirmation,
    editingJobField, editJobNotes, editJobType, editJobPrice,
    showSeriesModal, savingJobEdit,
    showNotifyClientModal, notifyType, sendingNotify,
    showBatchReminderModal, batchSelectedJobs, batchSending,
    batchProgress, batchResults,
    showMoreActions,
  } = modals;

  // Setter wrappers for backward compatibility with existing code
  const setShowAssignModal = (v) => setModal({ showAssignModal: v });
  const setJobToAssign = (v) => setModal({ jobToAssign: v });
  const setSelectedTeamMembers = (v) => {
    if (typeof v === 'function') setModal({ selectedTeamMembers: v(modals.selectedTeamMembers) });
    else setModal({ selectedTeamMembers: v });
  };
  const setShowRescheduleModal = (v) => setModal({ showRescheduleModal: v });
  const setRescheduleData = (v) => setModal({ rescheduleData: typeof v === 'function' ? v(modals.rescheduleData) : v });
  const setShowAddJobModal = (v) => setModal({ showAddJobModal: v });
  const setShowRecurringModal = (v) => setModal({ showRecurringModal: v });
  const setEditingJobForRecurring = (v) => setModal({ editingJobForRecurring: v });
  const setShowShiftScheduler = (v) => setModal({ showShiftScheduler: v });
  const setShowDuplicateWarning = (v) => setModal({ showDuplicateWarning: v });
  const setDuplicateClients = (v) => setModal({ duplicateClients: v });
  const setPendingJobData = (v) => setModal({ pendingJobData: v });
  const setSendingReminder = (v) => setModal({ sendingReminder: v });
  const setShowReminderModal = (v) => setModal({ showReminderModal: v });
  const setShowTimeOffManager = (v) => setModal({ showTimeOffManager: v });
  const setShowRunningBehindModal = (v) => setModal({ showRunningBehindModal: v });
  const setRunningBehindDelay = (v) => setModal({ runningBehindDelay: v });
  const setRunningBehindCustom = (v) => setModal({ runningBehindCustom: v });
  const setRunningBehindNote = (v) => setModal({ runningBehindNote: v });
  const setSendingRunningBehind = (v) => setModal({ sendingRunningBehind: v });
  const setShowConfirmationPrompt = (v) => setModal({ showConfirmationPrompt: v });
  const setConfirmationJobData = (v) => setModal({ confirmationJobData: v });
  const setSendingConfirmation = (v) => setModal({ sendingConfirmation: v });
  const setEditingJobField = (v) => setModal({ editingJobField: v });
  const setShowSeriesModal = (v) => setModal({ showSeriesModal: v });
  const setSavingJobEdit = (v) => setModal({ savingJobEdit: v });
  const setShowNotifyClientModal = (v) => setModal({ showNotifyClientModal: v });
  const setNotifyType = (v) => setModal({ notifyType: v });
  const setSendingNotify = (v) => setModal({ sendingNotify: v });
  const setShowBatchReminderModal = (v) => setModal({ showBatchReminderModal: v });
  const setBatchSelectedJobs = (v) => {
    if (typeof v === 'function') setModal({ batchSelectedJobs: v(modals.batchSelectedJobs) });
    else setModal({ batchSelectedJobs: v });
  };
  const setBatchSending = (v) => setModal({ batchSending: v });
  const setBatchResults = (v) => setModal({ batchResults: v });

  // Legacy Add Job form data kept as useState (complex nested object)
  const [newJobData, setNewJobData] = useState({
    clientId: '',
    clientName: '',
    address: '',
    city: 'Charlottesville',
    state: 'VA',
    zip: '',
    date: selectedDate,
    scheduledTime: '09:00',
    duration: 2.5,
    serviceType: 'one_time',
    price: 150,
    notes: ''
  });

  // Save job field — handles series logic
  const handleSaveJobField = async (field, value) => {
    if (!selectedJob) return;
    const hasRecurring = selectedJob.recurringScheduleId || selectedJob.isRecurring;

    // For notes: always check if client has future jobs (not just recurring)
    if (field === 'notes') {
      const hasFuture = state.jobs.some(j =>
        j.clientId === selectedJob.clientId &&
        j.date > selectedJob.date &&
        j.status !== 'cancelled' &&
        j.id !== selectedJob.id
      );
      if (hasFuture) {
        setModal({ showSeriesModal: { field, value, job: selectedJob } });
        return;
      }
    } else if (hasRecurring && field === 'serviceType') {
      setModal({ showSeriesModal: { field, value, job: selectedJob } });
      return;
    }

    // Just update this one job
    await saveJobFieldDirect(selectedJob.id, field, value);
  };

  const saveJobFieldDirect = async (jobId, field, value) => {
    setModal({ savingJobEdit: true });
    try {
      await updateJob(jobId, { [field]: value });
      setSelectedJob(prev => prev ? { ...prev, [field]: value } : null);
    } catch (e) {
      console.error('Failed to save:', e);
      toast.error('Failed to save. Try again.');
    }
    setModal({ savingJobEdit: false, editingJobField: null });
  };

  const handleSeriesUpdate = async (scope) => {
    if (!showSeriesModal) return;
    const { field, value, job } = showSeriesModal;
    setModal({ savingJobEdit: true });

    try {
      if (scope === 'this') {
        await saveJobFieldDirect(job.id, field, value);
      } else if (field === 'notes') {
        // Update all future jobs for this client (regardless of series)
        const futureJobs = state.jobs.filter(j =>
          j.clientId === job.clientId &&
          j.date > job.date &&
          j.status !== 'cancelled' &&
          j.id !== job.id
        );
        // Update current job
        await updateJob(job.id, { [field]: value });
        for (const fj of futureJobs) {
          await updateJob(fj.id, { [field]: value });
        }
        // Also update recurring schedule template if applicable
        if (job.recurringScheduleId) {
          await setDoc(doc(db, 'recurringSchedules', job.recurringScheduleId), { [field]: value }, { merge: true });
        }
        setSelectedJob(prev => prev ? { ...prev, [field]: value } : null);
      } else {
        // For non-notes fields, use series logic
        const seriesJobs = state.jobs.filter(j =>
          j.recurringScheduleId === job.recurringScheduleId &&
          j.date >= job.date &&
          j.status === 'scheduled'
        );
        for (const sj of seriesJobs) {
          await updateJob(sj.id, { [field]: value });
        }
        if (job.recurringScheduleId) {
          await setDoc(doc(db, 'recurringSchedules', job.recurringScheduleId), { [field]: value }, { merge: true });
        }
        setSelectedJob(prev => prev ? { ...prev, [field]: value } : null);
      }
    } catch (e) {
      console.error('Series update failed:', e);
      toast.error('Failed to update series. Try again.');
    }

    setModal({ savingJobEdit: false, showSeriesModal: null, editingJobField: null });
  };

  // Client notification handler
  const handleNotifyClient = async () => {
    if (!showNotifyClientModal) return;
    const job = showNotifyClientModal;
    const client = state.clients.find(c => c.id === job.clientId);
    if (!client?.email) {
      toast.error('No email found for this client.');
      return;
    }
    setModal({ sendingNotify: true });
    try {
      const dateStr = format(parseISO(job.date), 'EEEE, MMMM d, yyyy');
      const timeStr = formatJobTime(job.scheduledTime);
      const subject = notifyType === 'booked'
        ? 'Appointment Confirmed - Two Sisters Cleaning'
        : 'Appointment Rescheduled - Two Sisters Cleaning';
      const body = notifyType === 'booked'
        ? `<div style="font-family:sans-serif;max-width:600px;margin:0 auto">
            <h2 style="color:#2c5f4e">Your Appointment is Confirmed! ✨</h2>
            <p>Hi ${client.firstName || client.name?.split(' ')[0] || 'there'},</p>
            <p>Your cleaning appointment has been booked:</p>
            <div style="background:#f8f7f5;padding:16px;border-radius:8px;margin:16px 0">
              <p style="margin:4px 0"><strong>Date:</strong> ${dateStr}</p>
              <p style="margin:4px 0"><strong>Time:</strong> ${timeStr}</p>
              <p style="margin:4px 0"><strong>Address:</strong> ${job.address}</p>
              <p style="margin:4px 0"><strong>Service:</strong> ${serviceTypes[job.serviceType]?.label || job.serviceType}</p>
            </div>
            <p>If you need to make any changes, please contact us.</p>
            <p style="margin-top:24px;color:#6b7280;font-size:14px">— Two Sisters Cleaning & Organizing</p>
          </div>`
        : `<div style="font-family:sans-serif;max-width:600px;margin:0 auto">
            <h2 style="color:#2c5f4e">Appointment Update</h2>
            <p>Hi ${client.firstName || client.name?.split(' ')[0] || 'there'},</p>
            <p>Your cleaning appointment has been rescheduled:</p>
            <div style="background:#f8f7f5;padding:16px;border-radius:8px;margin:16px 0">
              <p style="margin:4px 0"><strong>New Date:</strong> ${dateStr}</p>
              <p style="margin:4px 0"><strong>Time:</strong> ${timeStr}</p>
              <p style="margin:4px 0"><strong>Address:</strong> ${job.address}</p>
            </div>
            <p>If this doesn't work for you, please let us know and we'll find a better time.</p>
            <p style="margin-top:24px;color:#6b7280;font-size:14px">— Two Sisters Cleaning & Organizing</p>
          </div>`;
      await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'raw', data: { to: client.email, subject, html: body } })
      });
      toast.success(`${notifyType === 'booked' ? 'Booking' : 'Reschedule'} notification sent to ${client.email}`);
    } catch (e) {
      console.error('Notify failed:', e);
      toast.error('Failed to send notification.');
    }
    setModal({ sendingNotify: false, showNotifyClientModal: null });
  };

  // Open batch reminder modal — pre-select eligible jobs
  const openBatchReminderModal = () => {
    const eligible = todayJobs.filter(j =>
      j.status !== 'cancelled' && j.status !== 'completed'
    );
    const selections = {};
    eligible.forEach(j => {
      const client = state.clients.find(c => c.id === j.clientId);
      // Pre-select jobs that have a client email or phone and haven't had a reminder sent yet
      selections[j.id] = (!!client?.email || !!client?.phone) && !j.reminderSent;
    });
    setModal({
      batchSelectedJobs: selections,
      batchResults: null,
      batchProgress: { sent: 0, failed: 0, skipped: 0, total: 0 },
      batchSending: false,
      showBatchReminderModal: true,
    });
  };

  // Send batch appointment reminders via email and SMS
  const handleBatchSendReminders = async () => {
    const selectedIds = Object.entries(batchSelectedJobs)
      .filter(([, selected]) => selected)
      .map(([id]) => id);

    if (selectedIds.length === 0) return;

    setModal({ batchSending: true, batchResults: null, batchProgress: { sent: 0, failed: 0, skipped: 0, total: selectedIds.length } });

    const results = [];

    for (const jobId of selectedIds) {
      const job = todayJobs.find(j => j.id === jobId);
      if (!job) {
        setModal({ batchProgress: { ...modals.batchProgress, skipped: modals.batchProgress.skipped + 1 } });
        results.push({ jobId, status: 'skipped', reason: 'Job not found' });
        continue;
      }

      const client = state.clients.find(c => c.id === job.clientId);
      if (!client?.email && !client?.phone) {
        setModal({ batchProgress: { ...modals.batchProgress, skipped: modals.batchProgress.skipped + 1 } });
        results.push({ jobId, clientName: job.clientName, status: 'skipped', reason: 'No email or phone' });
        continue;
      }

      try {
        const commLogId = `log_${Date.now()}_${jobId}`;
        let emailSent = false;
        let smsSent = false;

        // Send email if client has email
        if (client.email) {
          try {
            await sendAppointmentReminder({
              clientId: client.id,
              commLogId,
              clientEmail: client.email,
              clientName: job.clientName,
              date: format(parseISO(job.date), 'EEEE, MMMM d, yyyy'),
              time: formatJobTime(job.scheduledTime),
              address: `${job.address}, ${job.city || 'Charlottesville'}, ${job.state || 'VA'}`
            });
            emailSent = true;
          } catch (emailErr) {
            console.error(`Email reminder failed for job ${jobId}:`, emailErr);
          }
        }

        // Send SMS if client has phone
        if (client.phone) {
          try {
            const smsResult = await sendSMS('appointment_reminder', {
              phone: client.phone,
              date: format(parseISO(job.date), 'EEEE, MMMM d'),
              time: formatJobTime(job.scheduledTime),
              portalLink: 'https://keelgroup.app/portal'
            });
            smsSent = smsResult.success;
            if (!smsResult.success) {
              console.error(`SMS reminder failed for job ${jobId}:`, smsResult.error);
            }
          } catch (smsErr) {
            console.error(`SMS reminder failed for job ${jobId}:`, smsErr);
          }
        }

        if (!emailSent && !smsSent) {
          throw new Error('Both email and SMS failed');
        }

        await logClientCommunication(client.id, {
          id: emailSent ? commLogId : undefined,
          type: emailSent && smsSent ? 'both' : emailSent ? 'email' : 'sms',
          category: 'appointment_reminder',
          subject: 'Appointment Reminder',
          recipient: emailSent ? client.email : client.phone,
          status: 'sent',
          details: { jobId: job.id, jobDate: job.date, batchSend: true, emailSent, smsSent }
        });

        await updateJob(job.id, {
          reminderSent: true,
          reminderSentAt: new Date().toISOString(),
          reminderMethod: emailSent && smsSent ? 'both' : emailSent ? 'email' : 'sms'
        });

        setModal({ batchProgress: { ...modals.batchProgress, sent: modals.batchProgress.sent + 1 } });
        results.push({ jobId, clientName: job.clientName, email: client.email, phone: client.phone, emailSent, smsSent, status: 'sent' });
      } catch (error) {
        console.error(`Failed to send reminder for job ${jobId}:`, error);

        if (client.email || client.phone) {
          await logClientCommunication(client.id, {
            type: 'email',
            category: 'appointment_reminder',
            subject: 'Appointment Reminder',
            recipient: client.email || client.phone,
            status: 'failed',
            error: error.message,
            details: { jobId: job.id, jobDate: job.date, batchSend: true }
          });
        }

        setModal({ batchProgress: { ...modals.batchProgress, failed: modals.batchProgress.failed + 1 } });
        results.push({ jobId, clientName: job.clientName, email: client.email, phone: client.phone, status: 'failed', error: error.message });
      }
    }

    setModal({ batchResults: results, batchSending: false });
  };

  // Send appointment reminder email
  const handleSendReminder = async (job, e) => {
    e.stopPropagation();
    
    // Get client
    const client = state.clients.find(c => c.id === job.clientId);
    if (!client?.email && !client?.phone) {
      toast.error('No email or phone found for this client');
      return;
    }

    setModal({ sendingReminder: job.id });
    
    let emailSent = false;
    let smsSent = false;
    const commLogId = `log_${Date.now()}`;

    try {
      // Send email if client has email
      if (client.email) {
        try {
          await sendAppointmentReminder({
            clientId: client.id,
            commLogId,
            clientEmail: client.email,
            clientName: job.clientName,
            date: format(parseISO(job.date), 'EEEE, MMMM d, yyyy'),
            time: formatJobTime(job.scheduledTime),
            address: `${job.address}, ${job.city || 'Charlottesville'}, ${job.state || 'VA'}`
          });
          emailSent = true;
        } catch (emailErr) {
          console.error('Email reminder failed:', emailErr);
        }
      }

      // Send SMS if client has phone — no opt-in check for manual reminders
      if (client.phone) {
        try {
          const smsResult = await sendSMS('appointment_reminder', {
            phone: client.phone,
            date: format(parseISO(job.date), 'EEEE, MMMM d'),
            time: formatJobTime(job.scheduledTime),
            portalLink: 'https://keelgroup.app/portal'
          });
          smsSent = smsResult.success;
          if (!smsResult.success) {
            console.error('SMS reminder failed:', smsResult.error);
          }
        } catch (smsErr) {
          console.error('SMS reminder failed:', smsErr);
        }
      }

      if (!emailSent && !smsSent) {
        throw new Error('Both email and SMS failed');
      }

      // Log the communication
      await logClientCommunication(client.id, {
        id: emailSent ? commLogId : undefined,
        type: emailSent && smsSent ? 'both' : emailSent ? 'email' : 'sms',
        category: 'appointment_reminder',
        subject: 'Appointment Reminder',
        recipient: emailSent ? client.email : client.phone,
        status: 'sent',
        details: {
          jobId: job.id,
          jobDate: job.date,
          address: job.address,
          emailSent,
          smsSent
        }
      });
      
      // Mark reminder as sent in the job
      await updateJob(job.id, { reminderSent: true, reminderSentAt: new Date().toISOString() });
      
    } catch (error) {
      console.error('Failed to send reminder:', error);
      
      // Log failed communication
      await logClientCommunication(client.id, {
        type: 'email',
        category: 'appointment_reminder',
        subject: 'Appointment Reminder',
        recipient: client.email || client.phone,
        status: 'failed',
        error: error.message,
        details: { jobId: job.id, jobDate: job.date }
      });
      
      toast.error('Failed to send reminder. Please try again.');
    } finally {
      setModal({ sendingReminder: null });
    }
  };

  // State for creating invoice
  const [creatingInvoice, setCreatingInvoice] = useState(null);
  
  // State for sending welcome email
  const [sendingWelcome, setSendingWelcome] = useState(null);

  // Check if job already has an invoice
  const getJobInvoice = (jobId) => {
    return state.invoices.find(inv => inv.jobId === jobId);
  };
  
  // Check if this is the client's first appointment (no past completed jobs)
  const isFirstAppointment = useCallback((job) => {
    if (!job.clientId) return false;
    const clientJobs = state.jobs.filter(j => 
      j.clientId === job.clientId && 
      j.id !== job.id &&
      (j.status === 'completed' || (j.date && j.date < job.date))
    );
    return clientJobs.length === 0;
  }, [state.jobs]);
  
  // Check if this is an existing client (they have been a client before but this is first appointment in new system)
  const isExistingClient = useCallback((job, client) => {
    // If client was created before the new system launch OR has a status other than 'new'
    // OR they were migrated from the old system (check for migration flag or creation date)
    if (!client) return false;
    
    // Check if client has 'migrated' flag or was created before system launch
    if (client.migratedFromOldSystem) return true;
    
    // Check if client status indicates they're existing (not a fresh new lead)
    if (client.status === 'active' && !client.welcomeEmailSent) {
      // They're active but haven't received a welcome email - likely existing client
      return true;
    }
    
    return false;
  }, []);
  
  // Send welcome email
  const handleSendWelcomeEmail = async (job, forceExistingClient = false) => {
    const client = state.clients.find(c => c.id === job.clientId);
    if (!client || !client.email) {
      toast.error('Client email not found');
      return;
    }
    
    // Check if client has email notifications enabled (at least one category)
    const prefs = client.notificationPrefs || {};
    const hasAnyEmailEnabled = Object.values(prefs).some(cat => cat?.email);
    if (!hasAnyEmailEnabled && Object.keys(prefs).length > 0) {
      const confirm = window.confirm(
        `${client.firstName} ${client.lastName} has opted out of email notifications.\n\n` +
        'Do you want to send the welcome email anyway?'
      );
      if (!confirm) return;
    }
    
    setSendingWelcome(job.id);
    
    // Determine if this is an existing client being onboarded to the new system
    const existingClientFlag = forceExistingClient || isExistingClient(job, client);
    
    const welcomeLogId = `log_${Date.now()}`;
    try {
      await sendWelcomeEmail({
        clientId: client.id,
        commLogId: welcomeLogId,
        clientEmail: client.email,
        clientName: job.clientName || `${client.firstName} ${client.lastName}`,
        portalLink: 'https://keelgroup.app/portal',
        firstAppointmentDate: job.date,
        firstAppointmentTime: formatTimeForEmail(job.scheduledTime),
        address: `${job.address}, ${job.city || 'Charlottesville'}, ${job.state || 'VA'}`,
        isExistingClient: existingClientFlag
      });

      // Log the communication
      await logClientCommunication(client.id, {
        id: welcomeLogId,
        type: 'email',
        category: 'welcome',
        subject: existingClientFlag ? 'Portal Access' : 'Welcome Email',
        recipient: client.email,
        status: 'sent',
        details: {
          jobId: job.id,
          firstAppointment: job.date,
          isExistingClient: existingClientFlag
        }
      });
      
      // Mark welcome email as sent
      await updateJob(job.id, { welcomeEmailSent: true, welcomeEmailSentAt: new Date().toISOString() });
      
      toast.success('Welcome email sent successfully!');
    } catch (error) {
      console.error('Failed to send welcome email:', error);
      
      // Log failed communication
      await logClientCommunication(client.id, {
        type: 'email',
        category: 'welcome',
        subject: 'Welcome Email',
        recipient: client.email,
        status: 'failed',
        error: error.message,
        details: { jobId: job.id }
      });
      
      toast.error('Failed to send welcome email. Please try again.');
    } finally {
      setSendingWelcome(null);
    }
  };

  // Create invoice for a job manually
  const handleCreateInvoice = async (job) => {
    const client = state.clients.find(c => c.id === job.clientId);
    if (!client) {
      toast.error('Client not found for this job');
      return;
    }

    setCreatingInvoice(job.id);
    
    try {
      const invoice = await createInvoiceForJob(job, client);
      toast.success(`Invoice ${invoice.invoiceNumber} created! Go to Invoices to send it.`);
      navigate('/invoices');
    } catch (error) {
      console.error('Failed to create invoice:', error);
      toast.error('Failed to create invoice. Please try again.');
    } finally {
      setCreatingInvoice(null);
    }
  };

  // Subscribe to live GPS locations
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'gps_locations'),
      (snapshot) => {
        const locations = {};
        snapshot.docs.forEach(doc => {
          locations[doc.id] = { id: doc.id, ...doc.data() };
        });
        setLiveLocations(locations);
      },
      (error) => {
        console.error('Error listening to GPS locations:', error);
      }
    );
    return () => unsubscribe();
  }, []);

  // Subscribe to time records for selected date
  // Keyboard shortcut: P = print dispatch sheet
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        window.print();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'timeRecords'),
      (snapshot) => {
        const records = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(record => record.date === selectedDate);
        setTimeRecords(records);
      },
      (error) => {
        console.error('Error listening to time records:', error);
      }
    );
    return () => unsubscribe();
  }, [selectedDate]);

  // Refresh `now` every 60s so live clocked-in hours tick up in the goal tracker.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  const { isLoaded, loadError, retry } = useGoogleMaps();

  // Geocoded jobs state (stores lat/lng for jobs without coordinates)
  const [geocodedJobs, setGeocodedJobs] = useState({});

  const allTeamMembers = useMemo(() => {
    return state.employees.filter(emp => emp.status === 'active' || emp.status === 'onboarding').map((emp, idx) => ({
      ...emp,
      displayName: `${emp.firstName} ${emp.lastName.charAt(0)}.`,
      color: teamColors[idx % teamColors.length]
    }));
  }, [state.employees]);

  const todayJobs = useMemo(() => state.jobs.filter(job => job.date === selectedDate), [state.jobs, selectedDate]);

  // Geocode jobs that don't have coordinates
  useEffect(() => {
    if (!isLoaded || !window.google) return;

    const geocoder = new window.google.maps.Geocoder();
    
    todayJobs.forEach(job => {
      // Skip if already has coordinates or already geocoded
      if (job.lat && job.lng) return;
      if (geocodedJobs[job.id]) return;

      const address = `${job.address}, ${job.city || 'Charlottesville'}, ${job.state || 'VA'}`;
      
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          setGeocodedJobs(prev => ({
            ...prev,
            [job.id]: { lat: location.lat(), lng: location.lng() }
          }));
          
          // Also save to Firebase so we don't have to geocode again
          updateJob(job.id, {
            lat: location.lat(),
            lng: location.lng()
          }).catch(err => console.error('Failed to save geocode:', err));
        }
      });
    });
  }, [isLoaded, todayJobs, geocodedJobs, updateJob]);

  // Helper to get job coordinates (from job or geocoded cache)
  const getJobCoords = useCallback((job) => {
    if (job.lat && job.lng) return { lat: job.lat, lng: job.lng };
    if (geocodedJobs[job.id]) return geocodedJobs[job.id];
    return null;
  }, [geocodedJobs]);

  const unassignedJobs = useMemo(() => todayJobs.filter(job => !job.team || job.team.length === 0), [todayJobs]);
  const assignedJobs = useMemo(() => todayJobs.filter(job => job.team && job.team.length > 0), [todayJobs]);
  const unscheduledJobs = useMemo(() => state.jobs.filter(job => (job.status === 'unscheduled' || !job.date) && job.clientName && job.clientName.trim()), [state.jobs]);
  
  // Pending quote requests (estimates with status pending_review)
  const pendingQuoteRequests = useMemo(() => 
    state.estimates.filter(e => e.status === 'pending_review')
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [state.estimates]
  );

  const filteredJobs = useMemo(() => {
    let jobs = assignedJobs;
    if (selectedTeamFilter) jobs = jobs.filter(job => job.teamNames?.includes(selectedTeamFilter));
    if (statusFilter === 'in_progress') jobs = jobs.filter(job => job.status === 'in_progress');
    else if (statusFilter === 'completed') jobs = jobs.filter(job => job.status === 'completed');
    else if (statusFilter === 'unassigned') jobs = [];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      jobs = jobs.filter(job => job.clientName.toLowerCase().includes(query) || job.address.toLowerCase().includes(query));
    }
    return jobs;
  }, [assignedJobs, selectedTeamFilter, statusFilter, searchQuery]);

  const jobsByTeam = useMemo(() => groupJobsByTeam(filteredJobs), [filteredJobs]);

  const todayTeamMembers = useMemo(() => {
    const members = new Set();
    assignedJobs.forEach(job => job.teamNames?.forEach(name => members.add(name)));
    return Array.from(members);
  }, [assignedJobs]);

  // Employees scheduled on the shift schedule for the selected date.
  // Falls back to all active members on days where no shifts have been
  // entered, so dispatch keeps working before the schedule is built out.
  const scheduledTeamMembers = useMemo(() => {
    const dayShifts = (state.shifts || []).filter(
      s => s.date === selectedDate && s.status !== 'cancelled'
    );
    if (dayShifts.length === 0) return allTeamMembers;
    const scheduledIds = new Set(dayShifts.map(s => s.employeeId));
    return allTeamMembers.filter(emp => scheduledIds.has(emp.id));
  }, [allTeamMembers, state.shifts, selectedDate]);

  // True staffing count for the Daily Goal Tracker: distinct active team
  // members who actually have a (non-cancelled) shift on the selected date.
  // Unlike scheduledTeamMembers, this does NOT fall back to the full roster,
  // so the Staffing metric reads 0 when nobody is on the schedule.
  const scheduledMemberCount = useMemo(() => {
    const scheduledIds = new Set(
      (state.shifts || [])
        .filter(s => s.date === selectedDate && s.status !== 'cancelled')
        .map(s => s.employeeId)
    );
    return allTeamMembers.filter(emp => scheduledIds.has(emp.id)).length;
  }, [allTeamMembers, state.shifts, selectedDate]);

  const availableTeamMembers = useMemo(() =>
    scheduledTeamMembers.filter(emp => !todayTeamMembers.includes(emp.displayName)),
    [scheduledTeamMembers, todayTeamMembers]
  );

  // Get clocked in employees for today
  const clockedInEmployees = useMemo(() => {
    return timeRecords
      .filter(record => record.status === 'clocked_in')
      .map(record => {
        const employee = state.employees.find(e => e.id === record.employeeId);
        return {
          ...record,
          employee,
          displayName: employee ? `${employee.firstName} ${employee.lastName?.charAt(0) || ''}.` : record.employeeName
        };
      });
  }, [timeRecords, state.employees]);

  // Filter live locations to only show employees who are currently clocked in
  const filteredLiveLocations = useMemo(() => {
    const clockedInIds = new Set(clockedInEmployees.map(e => e.employeeId));
    const filtered = {};
    Object.entries(liveLocations).forEach(([id, loc]) => {
      if (clockedInIds.has(id)) {
        filtered[id] = loc;
      }
    });
    return filtered;
  }, [liveLocations, clockedInEmployees]);

  // Get all time records for today (clocked in and out)
  const todayTimeRecords = useMemo(() => {
    return timeRecords.map(record => {
      const employee = state.employees.find(e => e.id === record.employeeId);
      return {
        ...record,
        employee,
        displayName: employee ? `${employee.firstName} ${employee.lastName?.charAt(0) || ''}.` : record.employeeName
      };
    }).sort((a, b) => {
      // Sort by clock in time
      if (a.clockInTime && b.clockInTime) {
        return new Date(a.clockInTime) - new Date(b.clockInTime);
      }
      return 0;
    });
  }, [timeRecords, state.employees]);

  const hoursPerMember = useMemo(() => {
    const hours = {};
    assignedJobs.forEach(job => job.teamNames?.forEach(name => { hours[name] = (hours[name] || 0) + job.duration; }));
    return hours;
  }, [assignedJobs]);

  const stats = useMemo(() => ({
    total: todayJobs.length,
    unassigned: unassignedJobs.length,
    completed: todayJobs.filter(j => j.status === 'completed').length,
    inProgress: todayJobs.filter(j => j.status === 'in_progress').length,
    revenue: todayJobs.reduce((sum, j) => sum + j.price, 0),
    totalMiles: Object.values(jobsByTeam).reduce((sum, team) => sum + calculateRouteStats(team.jobs).totalMiles, 0)
  }), [todayJobs, unassignedJobs, jobsByTeam]);

  // Daily Goal Tracker metrics — all derived from the day's revenue + scheduled staff.
  // Goal: $400 revenue per staff member per 8-hour day (= $50 revenue per labor hour).
  const goalStats = useMemo(() => {
    const revenue = stats.revenue;
    const girlsScheduled = scheduledMemberCount;

    // Revenue earned so far today (completed jobs) — drives the live % to target.
    const completedRevenue = todayJobs
      .filter(j => j.status === 'completed')
      .reduce((sum, j) => sum + (j.price || 0), 0);

    // Actual clocked hours: completed records use hoursWorked; still-clocked-in use live elapsed.
    const actualClockedHours = timeRecords.reduce((sum, r) => {
      if (r.status === 'clocked_in' && r.clockInTime) {
        return sum + Math.max(0, (now - new Date(r.clockInTime).getTime()) / 3600000);
      }
      return sum + (r.hoursWorked || 0);
    }, 0);

    const girlsNeeded = Math.round(revenue / REVENUE_GOAL_PER_STAFF);
    const goalHours = revenue / REVENUE_GOAL_PER_HOUR;
    const scheduledHours = girlsScheduled * HOURS_PER_STAFF_DAY;
    const revenuePerStaff = girlsScheduled > 0 ? revenue / girlsScheduled : 0;
    const dailyRevenueTarget = girlsScheduled * REVENUE_GOAL_PER_STAFF;

    // Safe percentage helper — avoids NaN/Infinity on empty days.
    const pct = (num, den) => (den > 0 ? (num / den) * 100 : 0);

    return {
      revenue,
      completedRevenue,
      dailyRevenueTarget,
      // If nobody is scheduled there's no target to pace against — fall back to booked revenue.
      pctToTarget: dailyRevenueTarget > 0 ? pct(completedRevenue, dailyRevenueTarget) : 0,
      pctBooked: pct(revenue, dailyRevenueTarget),
      revenuePerStaff,
      pctRevenuePerStaff: pct(revenuePerStaff, REVENUE_GOAL_PER_STAFF),
      girlsScheduled,
      girlsNeeded,
      pctStaffing: girlsNeeded > 0 ? pct(girlsScheduled, girlsNeeded) : (girlsScheduled > 0 ? 100 : 0),
      goalHours,
      scheduledHours,
      actualClockedHours,
      pctHoursToGoal: pct(actualClockedHours, goalHours),
      pctHoursVsScheduled: pct(actualClockedHours, scheduledHours),
    };
  }, [stats.revenue, scheduledMemberCount, todayJobs, timeRecords, now]);

  const getTeamColor = useCallback((name) => allTeamMembers.find(m => m.displayName === name)?.color || '#6b7280', [allTeamMembers]);

  const goToPrevDay = () => setSelectedDate(format(subDays(parseISO(selectedDate), 1), 'yyyy-MM-dd'));
  const goToNextDay = () => setSelectedDate(format(addDays(parseISO(selectedDate), 1), 'yyyy-MM-dd'));
  const getDateDisplay = (offset) => {
    const date = addDays(parseISO(selectedDate), offset);
    return { date: format(date, 'yyyy-MM-dd'), label: format(date, 'EEE d'), isToday: format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd') };
  };

  const openAssignModal = (job) => { setJobToAssign(job); setSelectedTeamMembers(job.team || []); setShowAssignModal(true); };
  const toggleTeamMember = (empId) => setSelectedTeamMembers(prev => prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]);

  const sendRoutesToSlack = async () => {
    const teams = Object.values(jobsByTeam).filter(t => t.teamIds.length > 0 && t.jobs.length > 0);
    if (teams.length === 0) {
      toast.error('No assigned routes for this day to send.');
      return;
    }
    if (!window.confirm(`Send ${teams.length} team route${teams.length === 1 ? '' : 's'} to Slack?`)) {
      return;
    }

    setIsSendingRoutes(true);
    const dateLabel = format(parseISO(selectedDate), 'EEE, MMM d');
    let sent = 0;
    let failed = 0;

    for (const team of teams) {
      const jobs = team.jobs.map(j => ({
        time: formatJobTime?.(j) || j.scheduledTime || '',
        clientName: j.clientName || 'Client',
        address: j.address || j.location || '',
        duration: formatDuration?.(j.duration) || (j.duration ? `${j.duration}h` : '')
      }));

      // Resolve channel: prefer per-employee slackUserId DMs if available, else default channel
      const slackUserIds = team.teamIds
        .map(id => getEmployeeById(id)?.slackUserId)
        .filter(Boolean);

      const targets = slackUserIds.length > 0 ? slackUserIds : [undefined]; // undefined = default channel
      let teamSent = 0;
      for (const channelId of targets) {
        const result = await postTeamRoute({
          teamNames: team.teamNames,
          date: dateLabel,
          jobs,
          channelId
        });
        if (result.success) teamSent++;
      }
      if (teamSent > 0) sent++;
      else failed++;
    }

    setIsSendingRoutes(false);
    if (failed === 0) {
      toast.success(`Sent ${sent} route${sent === 1 ? '' : 's'} to Slack`);
    } else if (sent === 0) {
      toast.error('Failed to send routes. Check Slack settings.');
    } else {
      toast.error(`Sent ${sent} of ${sent + failed} routes — ${failed} failed.`);
    }
  };

  const saveTeamAssignment = async () => {
    if (!jobToAssign) return;
    const selectedEmps = allTeamMembers.filter(emp => selectedTeamMembers.includes(emp.id));
    const teamNames = selectedEmps.map(emp => emp.displayName);
    const oldTeamNames = jobToAssign.teamNames || [];

    // Build a consistent routeId from sorted team IDs so profitability grouping stays in sync.
    // If the job already has a routeId from RouteBuilder, keep it — only generate one if missing or team changed.
    const sortedIds = [...selectedTeamMembers].sort();
    const teamRouteId = sortedIds.length > 0 ? `team_${sortedIds.join('_')}` : null;

    // Determine routeId: keep existing routeId if team didn't actually change, otherwise derive from new team
    const teamChanged = JSON.stringify([...(jobToAssign.team || [])].sort()) !== JSON.stringify(sortedIds);
    const routeId = teamChanged ? teamRouteId : (jobToAssign.routeId || teamRouteId);

    // Build a human-readable route name from first names
    const routeName = selectedEmps.length > 0
      ? selectedEmps.map(emp => emp.displayName?.split(' ')[0] || '').filter(Boolean).join(' & ')
      : null;

    await updateJob(jobToAssign.id, {
      team: selectedTeamMembers,
      teamNames,
      routeId,
      routeName
    });
    logDispatchActivity({
      action: 'team_assigned',
      description: `Assigned ${teamNames.join(' & ')} to ${jobToAssign.clientName}`,
      jobId: jobToAssign.id,
      jobDate: jobToAssign.date || selectedDate,
      details: { from: oldTeamNames.join(', ') || 'Unassigned', to: teamNames.join(', '), client: jobToAssign.clientName }
    });
    // Fan out to Slack (#all-staff by default unless a crew channel is set per team)
    if (teamChanged && teamNames.length > 0) {
      postJobAssigned({
        clientName: jobToAssign.clientName,
        jobAddress: jobToAssign.address || jobToAssign.location || '',
        time: jobToAssign.time || formatJobTime?.(jobToAssign) || '',
        teamNames
      }).catch(err => console.error('Slack job-assignment post failed:', err));
    }
    setShowAssignModal(false); setJobToAssign(null); setSelectedTeamMembers([]);
    if (selectedJob?.id === jobToAssign.id) setSelectedJob({ ...selectedJob, team: selectedTeamMembers, teamNames, routeId, routeName });
  };

  // Calculate end time based on duration
  const calculateEndTime = (startTime, duration) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + (duration * 60);
    const endHours = Math.floor(totalMinutes / 60);
    const endMins = totalMinutes % 60;
    return `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
  };

  // Open Add Job Modal - NOW uses recurring modal
  const openAddJobModal = () => {
    setEditingJobForRecurring(null);
    setShowRecurringModal(true);
  };

  // Open Make Recurring Modal for an existing job
  const openMakeRecurringModal = (job) => {
    setEditingJobForRecurring(job);
    setShowRecurringModal(true);
  };

  // Handle save from recurring modal
  const handleRecurringModalSave = async (data) => {
    const { jobsToCreate, recurringOptions, ...jobData } = data;
    
    // Calculate end time helper
    const calcEndTime = (startTime, duration) => {
      const [hours, minutes] = startTime.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes + (duration * 60);
      const endHours = Math.floor(totalMinutes / 60);
      const endMins = totalMinutes % 60;
      return `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
    };

    // If no clientId exists (manual entry), check for duplicates or create new client
    if (!jobData.clientId || jobData.clientId.startsWith('manual_')) {
      // Parse the name - try to split into first/last
      const nameParts = jobData.clientName.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      // Check for duplicates
      const duplicates = findDuplicateClients({ firstName, lastName });
      
      if (duplicates.length > 0) {
        // Store pending data and show warning
        setPendingJobData({
          jobsToCreate,
          recurringOptions,
          ...jobData,
          parsedFirstName: firstName,
          parsedLastName: lastName,
          calcEndTime
        });
        setDuplicateClients(duplicates);
        setShowDuplicateWarning(true);
        return;
      }
      
      // No duplicates, create new client and proceed
      await createJobsWithNewClient(data, { firstName, lastName }, calcEndTime);
    } else {
      // Has existing clientId, just create jobs
      await createJobsDirectly(data, jobData.clientId, calcEndTime);
    }
  };

  // Create jobs with a new client record
  const createJobsWithNewClient = async (data, parsedName, calcEndTime) => {
    const { jobsToCreate, recurringOptions, ...jobData } = data;
    
    try {
      // Create the new client
      const newClient = await addClient({
        firstName: parsedName.firstName,
        lastName: parsedName.lastName,
        status: 'active'
      });

      // Create property if address provided
      if (jobData.address) {
        await addProperty({
          clientId: newClient.id,
          addressLine1: jobData.address,
          city: jobData.city || 'Charlottesville',
          state: jobData.state || 'VA',
          zipCode: jobData.zip || '',
          isPrimary: true
        });
      }

      // Create all the jobs with the new client
      for (const jobPreview of jobsToCreate) {
        // Calculate routeId from team so route-based reports and tips work correctly
        const teamIds = jobData.team || [];
        const sortedTeamIds = [...teamIds].sort();
        const jobRouteId = sortedTeamIds.length > 0 ? `team_${sortedTeamIds.join('_')}` : null;
        const jobRouteName = (jobData.teamNames || []).length > 0
          ? (jobData.teamNames || []).map(n => n.split(' ')[0]).filter(Boolean).join(' & ')
          : null;

        const newJob = {
          clientId: newClient.id,
          clientName: `${parsedName.firstName} ${parsedName.lastName}`.trim(),
          address: jobData.address,
          city: jobData.city,
          state: jobData.state,
          zip: jobData.zip,
          fullAddress: `${jobData.address}, ${jobData.city}, ${jobData.state} ${jobData.zip}`.trim(),
          date: jobPreview.date,
          scheduledTime: jobData.scheduledTime,
          scheduledEndTime: calcEndTime(jobData.scheduledTime, jobData.duration),
          duration: jobData.duration,
          serviceType: recurringOptions ? 'recurring' : jobData.serviceType,
          price: parseFloat(jobData.price) || 0,
          notes: jobData.notes,
          status: 'scheduled',
          team: teamIds,
          teamNames: jobData.teamNames || [],
          routeId: jobRouteId,
          routeName: jobRouteName,
          frequency: jobData.frequency,
          isRecurring: recurringOptions !== null,
          lat: null,
          lng: null,
        };
        await addJob(newJob);
      }

      setShowRecurringModal(false);
      setEditingJobForRecurring(null);
      
      const message = jobsToCreate.length > 1 
        ? `✅ Created ${jobsToCreate.length} recurring jobs for ${parsedName.firstName} ${parsedName.lastName}`
        : `✅ Job added for ${parsedName.firstName} ${parsedName.lastName}`;
      
      logDispatchActivity({
        action: 'job_added',
        description: jobsToCreate.length > 1
          ? `Added ${jobsToCreate.length} recurring jobs for ${parsedName.firstName} ${parsedName.lastName}`
          : `Added job for ${parsedName.firstName} ${parsedName.lastName} on ${selectedDate}`,
        jobDate: selectedDate,
        details: { client: `${parsedName.firstName} ${parsedName.lastName}` }
      });
      toast.success(message);
    } catch (error) {
      console.error('Error creating jobs:', error);
      toast.error('Failed to create jobs. Please try again.');
    }
  };

  // Create jobs directly (when client already selected)
  const createJobsDirectly = async (data, clientId, calcEndTime) => {
    const { jobsToCreate, recurringOptions, ...jobData } = data;
    
    try {
      for (const jobPreview of jobsToCreate) {
        // Calculate routeId from team so route-based reports and tips work correctly
        const teamIds = jobData.team || [];
        const sortedTeamIds = [...teamIds].sort();
        const jobRouteId = sortedTeamIds.length > 0 ? `team_${sortedTeamIds.join('_')}` : null;
        const jobRouteName = (jobData.teamNames || []).length > 0
          ? (jobData.teamNames || []).map(n => n.split(' ')[0]).filter(Boolean).join(' & ')
          : null;

        const newJob = {
          clientId: clientId,
          clientName: jobData.clientName,
          address: jobData.address,
          city: jobData.city,
          state: jobData.state,
          zip: jobData.zip,
          fullAddress: `${jobData.address}, ${jobData.city}, ${jobData.state} ${jobData.zip}`.trim(),
          date: jobPreview.date,
          scheduledTime: jobData.scheduledTime,
          scheduledEndTime: calcEndTime(jobData.scheduledTime, jobData.duration),
          duration: jobData.duration,
          serviceType: recurringOptions ? 'recurring' : jobData.serviceType,
          price: parseFloat(jobData.price) || 0,
          notes: jobData.notes,
          status: 'scheduled',
          team: teamIds,
          teamNames: jobData.teamNames || [],
          routeId: jobRouteId,
          routeName: jobRouteName,
          frequency: jobData.frequency,
          isRecurring: recurringOptions !== null,
          lat: null,
          lng: null,
        };
        await addJob(newJob);
      }

      setShowRecurringModal(false);
      setEditingJobForRecurring(null);
      
      const message = jobsToCreate.length > 1 
        ? `✅ Created ${jobsToCreate.length} recurring jobs for ${jobData.clientName}`
        : `✅ Job added for ${jobData.clientName}`;
      toast.success(message);
    } catch (error) {
      console.error('Error creating jobs:', error);
      toast.error('Failed to create jobs. Please try again.');
    }
  };

  // Handle using existing client from duplicate warning
  const handleUseExistingClientForJob = (existingClient) => {
    if (!pendingJobData) return;
    
    const { jobsToCreate, recurringOptions, calcEndTime, ...jobData } = pendingJobData;
    
    // Update client name to match existing client
    const clientName = `${existingClient.firstName} ${existingClient.lastName}`.trim();
    
    createJobsDirectly(
      { jobsToCreate, recurringOptions, ...jobData, clientName },
      existingClient.id,
      calcEndTime
    );
    
    setShowDuplicateWarning(false);
    setPendingJobData(null);
    setDuplicateClients([]);
  };

  // Handle creating new client anyway despite duplicates
  const handleCreateClientAnyway = async () => {
    if (!pendingJobData) return;
    
    const { parsedFirstName, parsedLastName, calcEndTime, ...restData } = pendingJobData;
    
    await createJobsWithNewClient(
      restData,
      { firstName: parsedFirstName, lastName: parsedLastName },
      calcEndTime
    );
    
    setShowDuplicateWarning(false);
    setPendingJobData(null);
    setDuplicateClients([]);
  };

  // Open Reschedule Modal
  const openRescheduleModal = (job) => {
    setRescheduleData({
      date: job.date,
      scheduledTime: job.scheduledTime || '09:00',
      duration: job.duration || 2.5
    });
    setShowRescheduleModal(true);
  };

  // Handle Reschedule
  const handleReschedule = async () => {
    if (!selectedJob) return;
    
    const endTime = calculateEndTime(rescheduleData.scheduledTime, rescheduleData.duration);
    const oldDate = selectedJob.date;
    const oldTime = selectedJob.scheduledTime;
    
    await updateJob(selectedJob.id, {
      date: rescheduleData.date,
      scheduledTime: rescheduleData.scheduledTime,
      scheduledEndTime: endTime,
      duration: rescheduleData.duration
    });
    
    logDispatchActivity({
      action: 'job_rescheduled',
      description: `Rescheduled ${selectedJob.clientName} to ${format(parseISO(rescheduleData.date), 'MMM d')} at ${rescheduleData.scheduledTime}`,
      jobId: selectedJob.id,
      jobDate: rescheduleData.date,
      details: { 
        from: `${oldDate} ${oldTime || ''}`.trim(), 
        to: `${rescheduleData.date} ${rescheduleData.scheduledTime}`,
        client: selectedJob.clientName 
      }
    });
    
    setShowRescheduleModal(false);

    // Undo support: save old values and offer to revert
    const jobId = selectedJob.id;
    const oldDuration = selectedJob.duration;
    const oldEndTime = selectedJob.scheduledEndTime;
    setSelectedJob(null);
    toast.undo(
      `Rescheduled ${selectedJob.clientName} to ${format(parseISO(rescheduleData.date), 'MMM d')}`,
      async () => {
        await updateJob(jobId, {
          date: oldDate,
          scheduledTime: oldTime,
          scheduledEndTime: oldEndTime,
          duration: oldDuration
        });
        toast.info('Reschedule reverted');
      }
    );
  };

  // Handle Add Job
  const handleAddJob = async () => {
    if (!newJobData.clientName || !newJobData.address) {
      toast.warning('Please fill in client name and address');
      return;
    }

    const endTime = calculateEndTime(newJobData.scheduledTime, newJobData.duration);
    
    // If we're scheduling an existing unscheduled job, update it instead of creating new
    if (newJobData.existingJobId) {
      await updateJob(newJobData.existingJobId, {
        date: newJobData.date,
        scheduledTime: newJobData.scheduledTime,
        scheduledEndTime: endTime,
        duration: newJobData.duration,
        status: 'scheduled',
        notes: newJobData.notes,
        price: parseFloat(newJobData.price) || 0
      });
      setShowAddJobModal(false);
      
      // Look up client to prompt for confirmation
      const client = newJobData.clientId ? state.clients.find(c => c.id === newJobData.clientId) : null;
      const savedJobInfo = {
        clientName: newJobData.clientName,
        clientId: newJobData.clientId,
        address: newJobData.address,
        date: newJobData.date,
        scheduledTime: newJobData.scheduledTime,
        client
      };
      
      setNewJobData({
        clientId: '',
        clientName: '',
        address: '',
        city: 'Charlottesville',
        state: 'VA',
        zip: '',
        date: selectedDate,
        scheduledTime: '09:00',
        duration: 2.5,
        serviceType: 'one_time',
        price: 150,
        notes: '',
        existingJobId: null
      });
      
      // Show confirmation prompt
      setConfirmationJobData(savedJobInfo);
      setShowConfirmationPrompt(true);
      return;
    }
    
    // Calculate routeId from team so route-based reports and tips work correctly
    const manualTeamIds = newJobData.team || [];
    const manualSortedIds = [...manualTeamIds].sort();
    const manualRouteId = manualSortedIds.length > 0 ? `team_${manualSortedIds.join('_')}` : null;
    const manualRouteName = (newJobData.teamNames || []).length > 0
      ? (newJobData.teamNames || []).map(n => n.split(' ')[0]).filter(Boolean).join(' & ')
      : null;

    const job = {
      clientId: newJobData.clientId || `manual_${Date.now()}`,
      clientName: newJobData.clientName,
      address: newJobData.address,
      city: newJobData.city,
      state: newJobData.state,
      zip: newJobData.zip,
      fullAddress: `${newJobData.address}, ${newJobData.city}, ${newJobData.state} ${newJobData.zip}`.trim(),
      date: newJobData.date,
      scheduledTime: newJobData.scheduledTime,
      scheduledEndTime: endTime,
      duration: newJobData.duration,
      serviceType: newJobData.serviceType,
      price: parseFloat(newJobData.price) || 0,
      notes: newJobData.notes,
      status: 'scheduled',
      team: manualTeamIds,
      teamNames: newJobData.teamNames || [],
      routeId: manualRouteId,
      routeName: manualRouteName,
      lat: null,
      lng: null,
      driveTimeFromPrev: null,
      driveDistanceFromPrev: null
    };

    await addJob(job);
    setShowAddJobModal(false);
    
    // Look up client to prompt for confirmation
    const client = newJobData.clientId ? state.clients.find(c => c.id === newJobData.clientId) : null;
    setConfirmationJobData({
      clientName: newJobData.clientName,
      clientId: newJobData.clientId || job.clientId,
      address: `${newJobData.address}, ${newJobData.city}, ${newJobData.state} ${newJobData.zip}`.trim(),
      date: newJobData.date,
      scheduledTime: newJobData.scheduledTime,
      client
    });
    setShowConfirmationPrompt(true);
  };

  const handleSendAppointmentConfirmation = async (method) => {
    if (!confirmationJobData) return;
    setSendingConfirmation(true);
    
    const client = confirmationJobData.client || state.clients.find(c => c.id === confirmationJobData.clientId);
    const formattedDate = format(parseISO(confirmationJobData.date), 'EEEE, MMMM d, yyyy');
    const formattedTime = formatJobTime(confirmationJobData.scheduledTime);
    
    try {
      if (method === 'text' && client?.phone) {
        await sendSMS('appointment_confirmation', {
          phone: client.phone,
          clientName: confirmationJobData.clientName,
          date: formattedDate,
          time: formattedTime
        });
        if (client?.id) {
          await logClientCommunication(client.id, {
            type: 'sms',
            category: 'appointment_confirmation',
            subject: 'Appointment Confirmation',
            recipient: client.phone,
            status: 'sent',
            details: { date: confirmationJobData.date, time: confirmationJobData.scheduledTime }
          });
        }
        toast.success('Confirmation text sent!');
      } else if (method === 'email' && client?.email) {
        await fetch('/.netlify/functions/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'raw',
            data: {
              to: client.email,
              subject: 'Appointment Confirmed - Two Sisters Cleaning',
              html: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                  <h2 style="color: #2c5f4e;">Your Appointment is Confirmed! ✨</h2>
                  <p>Hi ${confirmationJobData.clientName},</p>
                  <p>This is a confirmation of your upcoming cleaning appointment:</p>
                  <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
                    <p style="margin: 5px 0;"><strong>📅 Date:</strong> ${formattedDate}</p>
                    <p style="margin: 5px 0;"><strong>🕐 Time:</strong> ${formattedTime}</p>
                    <p style="margin: 5px 0;"><strong>📍 Address:</strong> ${confirmationJobData.address}</p>
                  </div>
                  <p>If you need to reschedule, please let us know as soon as possible.</p>
                  <p>Thank you for choosing Two Sisters Cleaning & Organizing!</p>
                  <p style="color: #64748b; font-size: 14px;">Two Sisters Cleaning & Organizing</p>
                </div>
              `
            }
          })
        });
        if (client?.id) {
          await logClientCommunication(client.id, {
            type: 'email',
            category: 'appointment_confirmation',
            subject: 'Appointment Confirmation',
            recipient: client.email,
            status: 'sent',
            details: { date: confirmationJobData.date, time: confirmationJobData.scheduledTime }
          });
        }
        toast.success('Confirmation email sent!');
      } else if (method === 'both' && client) {
        const promises = [];
        if (client.phone) {
          promises.push(sendSMS('appointment_confirmation', {
            phone: client.phone,
            clientName: confirmationJobData.clientName,
            date: formattedDate,
            time: formattedTime
          }));
        }
        if (client.email) {
          promises.push(fetch('/.netlify/functions/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'raw',
              data: {
                to: client.email,
                subject: 'Appointment Confirmed - Two Sisters Cleaning',
                html: `
                  <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                    <h2 style="color: #2c5f4e;">Your Appointment is Confirmed! ✨</h2>
                    <p>Hi ${confirmationJobData.clientName},</p>
                    <p>This is a confirmation of your upcoming cleaning appointment:</p>
                    <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
                      <p style="margin: 5px 0;"><strong>📅 Date:</strong> ${formattedDate}</p>
                      <p style="margin: 5px 0;"><strong>🕐 Time:</strong> ${formattedTime}</p>
                      <p style="margin: 5px 0;"><strong>📍 Address:</strong> ${confirmationJobData.address}</p>
                    </div>
                    <p>If you need to reschedule, please let us know as soon as possible.</p>
                    <p>Thank you for choosing Two Sisters Cleaning & Organizing!</p>
                    <p style="color: #64748b; font-size: 14px;">Two Sisters Cleaning & Organizing</p>
                  </div>
                `
              }
            })
          }));
        }
        await Promise.all(promises);
        if (client.id) {
          await logClientCommunication(client.id, {
            type: 'both',
            category: 'appointment_confirmation',
            subject: 'Appointment Confirmation',
            recipient: `${client.phone || ''} / ${client.email || ''}`,
            status: 'sent',
            details: { date: confirmationJobData.date, time: confirmationJobData.scheduledTime }
          });
        }
        toast.success('Confirmation sent via text & email!');
      }
    } catch (error) {
      console.error('Error sending confirmation:', error);
      toast.error('Failed to send confirmation. The job was still saved.');
    } finally {
      setSendingConfirmation(false);
      setShowConfirmationPrompt(false);
      setConfirmationJobData(null);
    }
  };

  const generateOptimizationSuggestions = () => {
    const suggestions = [];
    unassignedJobs.forEach(job => {
      let bestTeam = null, bestDistance = Infinity;
      Object.entries(jobsByTeam).forEach(([teamKey, team]) => {
        team.jobs.forEach(teamJob => {
          const dist = Math.sqrt(Math.pow(job.lat - teamJob.lat, 2) + Math.pow(job.lng - teamJob.lng, 2));
          if (dist < bestDistance) { bestDistance = dist; bestTeam = { teamKey, teamIds: team.teamIds, teamNames: team.teamNames }; }
        });
      });
      if (!bestTeam && availableTeamMembers.length >= 2) {
        bestTeam = { teamKey: `${availableTeamMembers[0].displayName} & ${availableTeamMembers[1].displayName}`, teamIds: [availableTeamMembers[0].id, availableTeamMembers[1].id], teamNames: [availableTeamMembers[0].displayName, availableTeamMembers[1].displayName] };
      }
      if (bestTeam) suggestions.push({ type: 'assign', job, suggestedTeam: bestTeam, reason: `Closest to ${bestTeam.teamKey}'s route`, impact: 'Minimizes drive time' });
    });
    
    const teamKeys = Object.keys(jobsByTeam);
    if (teamKeys.length >= 2) {
      for (let i = 0; i < teamKeys.length; i++) {
        for (let j = i + 1; j < teamKeys.length; j++) {
          const team1 = jobsByTeam[teamKeys[i]], team2 = jobsByTeam[teamKeys[j]];
          team1.jobs.forEach(job1 => {
            const currentDist1 = job1.driveDistanceFromPrev || 5;
            const distToTeam2 = Math.sqrt(Math.pow(job1.lat - (team2.jobs[0]?.lat || OFFICE.lat), 2) + Math.pow(job1.lng - (team2.jobs[0]?.lng || OFFICE.lng), 2)) * 50;
            if (distToTeam2 < currentDist1 * 0.7) {
              suggestions.push({ type: 'swap', job: job1, fromTeam: { teamKey: teamKeys[i], ...team1 }, toTeam: { teamKey: teamKeys[j], ...team2 }, reason: `${job1.clientName} is closer to ${teamKeys[j]}'s route`, impact: `Save ~${Math.round(currentDist1 - distToTeam2)} miles`, savings: Math.round((currentDist1 - distToTeam2) * 2) });
            }
          });
        }
      }
    }
    
    if (availableTeamMembers.length >= 2 && todayJobs.length > Object.keys(jobsByTeam).length * 3) {
      suggestions.push({ type: 'add_team', availableMembers: availableTeamMembers.slice(0, 2), reason: `${availableTeamMembers[0].displayName} & ${availableTeamMembers[1].displayName} are available`, impact: 'Could take on additional jobs' });
    }
    suggestions.sort((a, b) => (b.savings || 0) - (a.savings || 0));
    setOptimizationSuggestions(suggestions.slice(0, 5));
    setShowOptimizePanel(true);
  };

  const applyAssignSuggestion = async (s) => {
    const sortedIds = [...(s.suggestedTeam.teamIds || [])].sort();
    const routeId = sortedIds.length > 0 ? `team_${sortedIds.join('_')}` : null;
    const routeName = (s.suggestedTeam.teamNames || []).map(n => n.split(' ')[0]).filter(Boolean).join(' & ') || null;
    await updateJob(s.job.id, { team: s.suggestedTeam.teamIds, teamNames: s.suggestedTeam.teamNames, routeId, routeName });
    setOptimizationSuggestions(prev => prev.filter(x => x !== s));
  };

  const applySwapSuggestion = async (s) => {
    const sortedIds = [...(s.toTeam.teamIds || [])].sort();
    const routeId = sortedIds.length > 0 ? `team_${sortedIds.join('_')}` : null;
    const routeName = (s.toTeam.teamNames || []).map(n => n.split(' ')[0]).filter(Boolean).join(' & ') || null;
    await updateJob(s.job.id, { team: s.toTeam.teamIds, teamNames: s.toTeam.teamNames, routeId, routeName });
    setOptimizationSuggestions(prev => prev.filter(x => x !== s));
  };

  // Store calculated drive times from Google Directions
  const [routeDriveTimes, setRouteDriveTimes] = useState({});
  const [isCalculatingRoutes, setIsCalculatingRoutes] = useState(false);

  // Calculate drive time from office to a single address
  const calculateDriveTimeFromOffice = useCallback((jobId, address) => {
    if (!isLoaded || !window.google) return;
    
    const distanceService = new window.google.maps.DistanceMatrixService();
    distanceService.getDistanceMatrix({
      origins: [OFFICE.fullAddress],
      destinations: [address],
      travelMode: window.google.maps.TravelMode.DRIVING,
    }, (result, status) => {
      if (status === 'OK' && result.rows[0]?.elements[0]?.status === 'OK') {
        const element = result.rows[0].elements[0];
        setRouteDriveTimes(prev => ({
          ...prev,
          [jobId]: {
            duration: Math.round(element.duration.value / 60),
            distance: Math.round(element.distance.value / 1609.34 * 10) / 10
          }
        }));
      }
    });
  }, [isLoaded]);

  const calculateDirections = useCallback((teamKey, jobs) => {
    if (!isLoaded || !window.google || jobs.length === 0) return;
    const directionsService = new window.google.maps.DirectionsService();
    const waypoints = jobs.map(job => ({ location: job.fullAddress, stopover: true }));
    directionsService.route({ origin: OFFICE.fullAddress, destination: OFFICE.fullAddress, waypoints, optimizeWaypoints: false, travelMode: window.google.maps.TravelMode.DRIVING }, (result, status) => {
      if (status === 'OK') {
        setDirections(prev => ({ ...prev, [teamKey]: result }));
        
        // Extract drive times from the route legs
        const legs = result.routes[0].legs;
        const driveTimes = {};
        jobs.forEach((job, index) => {
          // leg[0] is office to first job, leg[1] is first job to second job, etc.
          const leg = legs[index];
          if (leg) {
            driveTimes[job.id] = {
              duration: Math.round(leg.duration.value / 60), // Convert seconds to minutes
              distance: Math.round(leg.distance.value / 1609.34 * 10) / 10 // Convert meters to miles
            };
          }
        });
        // Also get the return leg (last leg is back to office)
        const returnLeg = legs[legs.length - 1];
        if (returnLeg && jobs.length > 0) {
          driveTimes[`${jobs[jobs.length - 1].id}_return`] = {
            duration: Math.round(returnLeg.duration.value / 60),
            distance: Math.round(returnLeg.distance.value / 1609.34 * 10) / 10
          };
        }
        setRouteDriveTimes(prev => ({ ...prev, ...driveTimes }));
      }
    });
  }, [isLoaded]);

  // Calculate all routes button handler
  const calculateAllRoutes = useCallback(() => {
    if (!isLoaded || !window.google) {
      toast.info('Google Maps is still loading. Please wait.');
      return;
    }
    
    setIsCalculatingRoutes(true);
    
    // Calculate for each team
    Object.entries(jobsByTeam).forEach(([teamKey, team]) => {
      if (team.jobs.length > 0) {
        calculateDirections(teamKey, team.jobs);
      }
    });
    
    // Calculate drive time from office for each unassigned job
    unassignedJobs.forEach(job => {
      if (job.fullAddress && !routeDriveTimes[job.id]) {
        calculateDriveTimeFromOffice(job.id, job.fullAddress);
      }
    });
    
    setTimeout(() => setIsCalculatingRoutes(false), 2000);
  }, [isLoaded, jobsByTeam, unassignedJobs, routeDriveTimes, calculateDirections, calculateDriveTimeFromOffice]);

  // Auto-calculate directions for all teams when jobs change
  useEffect(() => {
    if (!isLoaded || !window.google) return;
    
    // Calculate directions for each team
    Object.entries(jobsByTeam).forEach(([teamKey, team]) => {
      if (team.jobs.length > 0) {
        // Always recalculate to get fresh times
        calculateDirections(teamKey, team.jobs);
      }
    });
    
    // Calculate for unassigned jobs too (drive time from office)
    unassignedJobs.forEach(job => {
      if (job.fullAddress && !routeDriveTimes[job.id]) {
        calculateDriveTimeFromOffice(job.id, job.fullAddress);
      }
    });
  }, [isLoaded, jobsByTeam, unassignedJobs, calculateDirections, calculateDriveTimeFromOffice]);

  // Calculate shift times using real drive times
  const calculateShiftTimesWithRealDrive = useCallback((jobs) => {
    if (!jobs || jobs.length === 0) return null;
    
    // Sort jobs by scheduled time
    const sortedJobs = [...jobs].sort((a, b) => a.scheduledTime.localeCompare(b.scheduledTime));
    
    const firstJob = sortedJobs[0];
    const lastJob = sortedJobs[sortedJobs.length - 1];
    
    // Get drive time to first job from calculated times, or use stored value, or default
    const firstJobCalcTime = routeDriveTimes[firstJob.id];
    const firstDriveTime = firstJobCalcTime?.duration ?? firstJob.driveTimeFromPrev ?? 15;
    
    // Parse first job time
    const [firstHour, firstMin] = firstJob.scheduledTime.split(':').map(Number);
    
    // Shift start = first job time - drive time - 15 min prep
    const shiftStartMinutes = (firstHour * 60 + firstMin) - firstDriveTime - 15;
    const startHour = Math.floor(shiftStartMinutes / 60);
    const startMin = shiftStartMinutes % 60;
    
    // Parse last job end time
    const [lastEndHour, lastEndMin] = lastJob.scheduledEndTime.split(':').map(Number);
    
    // Get return drive time
    const returnCalcTime = routeDriveTimes[`${lastJob.id}_return`];
    const lastJobCalcTime = routeDriveTimes[lastJob.id];
    const returnDriveTime = returnCalcTime?.duration ?? lastJobCalcTime?.duration ?? lastJob.driveTimeFromPrev ?? 15;
    
    // Shift end = last job end + return drive time
    const shiftEndMinutes = (lastEndHour * 60 + lastEndMin) + returnDriveTime;
    const endHour = Math.floor(shiftEndMinutes / 60);
    const endMin = shiftEndMinutes % 60;
    
    // Calculate total shift hours
    const totalShiftMinutes = shiftEndMinutes - shiftStartMinutes;
    const totalShiftHours = Math.round(totalShiftMinutes / 60 * 10) / 10;
    
    // Calculate total drive time for route
    let totalDriveMinutes = firstDriveTime;
    sortedJobs.forEach((job, idx) => {
      if (idx > 0) {
        const prevJob = sortedJobs[idx - 1];
        // For jobs after the first, drive time is from previous job
        const calcTime = routeDriveTimes[job.id];
        totalDriveMinutes += calcTime?.duration ?? job.driveTimeFromPrev ?? 10;
      }
    });
    totalDriveMinutes += returnDriveTime;
    
    // Format times
    const formatTime12 = (h, m) => {
      const hour12 = h % 12 || 12;
      const ampm = h >= 12 ? 'PM' : 'AM';
      return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
    };
    
    // Calculate "leave by" time (when team must leave office)
    const leaveByMinutes = (firstHour * 60 + firstMin) - firstDriveTime;
    const leaveByHour = Math.floor(leaveByMinutes / 60);
    const leaveByMin = leaveByMinutes % 60;
    
    return {
      shiftStart: formatTime12(startHour, startMin),
      leaveBy: formatTime12(leaveByHour, leaveByMin),
      shiftEnd: formatTime12(endHour, endMin),
      totalShiftHours,
      totalDriveMinutes,
      firstJobTime: formatJobTime(firstJob.scheduledTime),
      lastJobEnd: formatJobTime(lastJob.scheduledEndTime),
      prepTime: 15,
      firstDriveTime,
      returnDriveTime
    };
  }, [routeDriveTimes]);

  const renderMarkers = useCallback(() => {
    if (!isLoaded) return null;
    const markers = [
      <Marker key="office" position={{ lat: OFFICE.lat, lng: OFFICE.lng }} icon={{ url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><circle cx="20" cy="20" r="18" fill="#1a2634" stroke="white" stroke-width="3"/><text x="20" y="26" text-anchor="middle" fill="white" font-size="14" font-weight="bold">HQ</text></svg>`), scaledSize: new window.google.maps.Size(40, 40), anchor: new window.google.maps.Point(20, 20) }} title={OFFICE.name} />
    ];
    Object.entries(jobsByTeam).forEach(([teamKey, team], teamIndex) => {
      const color = routeColors[teamIndex % routeColors.length];
      team.jobs.forEach((job, jobIndex) => {
        if (job.lat && job.lng) markers.push(<Marker key={job.id} position={{ lat: job.lat, lng: job.lng }} icon={{ url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="14" fill="${color}" stroke="white" stroke-width="2"/><text x="16" y="21" text-anchor="middle" fill="white" font-size="12" font-weight="bold">${jobIndex + 1}</text></svg>`), scaledSize: new window.google.maps.Size(32, 32), anchor: new window.google.maps.Point(16, 16) }} title={`${job.clientName} - ${formatJobTime(job.scheduledTime)}`} onClick={() => setSelectedJob(job)} />);
      });
    });
    unassignedJobs.forEach(job => {
      if (job.lat && job.lng) markers.push(<Marker key={`unassigned-${job.id}`} position={{ lat: job.lat, lng: job.lng }} icon={{ url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="14" fill="#9ca3af" stroke="white" stroke-width="2"/><text x="16" y="21" text-anchor="middle" fill="white" font-size="12" font-weight="bold">?</text></svg>`), scaledSize: new window.google.maps.Size(32, 32), anchor: new window.google.maps.Point(16, 16) }} title={`UNASSIGNED: ${job.clientName}`} onClick={() => openAssignModal(job)} />);
    });
    return markers;
  }, [isLoaded, jobsByTeam, unassignedJobs]);

  if (state.loading && !state.initialized) {
    return (
      <div className="dispatch-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" />
          <p>Loading dispatch...</p>
        </div>
      </div>
    );
  }

  const timeOffToday = (state.timeOffRequests || []).filter(t =>
    (t.status === 'approved' || t.status === 'pending') &&
    t.startDate <= selectedDate && t.endDate >= selectedDate
  );
  const pendingTimeOff = (state.timeOffRequests || []).filter(t => t.status === 'pending');
  const pendingReminderCount = todayJobs.filter(j => j.status !== 'cancelled' && j.status !== 'completed' && !j.reminderSent).length;
  const dispatchLogCount = state.dispatchLog?.filter(e => e.jobDate === selectedDate).length || 0;

  return (
    <div className="dispatch-page" data-density={density}>
      {/* Shift Scheduler View */}
      {showShiftScheduler ? (
        <ShiftScheduler onClose={() => setShowShiftScheduler(false)} />
      ) : (
        <>
      <DispatchTopBar
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        view={view}
        setView={setView}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedTeamFilter={selectedTeamFilter}
        setSelectedTeamFilter={setSelectedTeamFilter}
        showTeamFilter={showTeamFilter}
        setShowTeamFilter={setShowTeamFilter}
        todayTeamMembers={todayTeamMembers}
        hoursPerMember={hoursPerMember}
        getTeamColor={getTeamColor}
        pendingReminderCount={pendingReminderCount}
        dispatchLogCount={dispatchLogCount}
        onOpenBatchReminders={openBatchReminderModal}
        onOpenActivityLog={() => setShowDispatchLog(true)}
        onPrint={() => window.print()}
        onQuickAdd={openAddJobModal}
        showMoreActions={showMoreActions}
        setShowMoreActions={(v) => setModal({ showMoreActions: typeof v === 'function' ? v(showMoreActions) : v })}
        onOpenShiftScheduler={() => setShowShiftScheduler(true)}
        onCalculateRoutes={calculateAllRoutes}
        isCalculatingRoutes={isCalculatingRoutes}
        onOptimizeRoutes={generateOptimizationSuggestions}
        onOpenAutoGroup={() => setShowAutoGroupModal(true)}
        onOpenTimeOff={() => setShowTimeOffManager(true)}
        onSendRoutesToSlack={sendRoutesToSlack}
        isSendingRoutes={isSendingRoutes}
        density={density}
        onToggleDensity={toggleDensity}
      />

      <DispatchStatStrip
        stats={stats}
        needsSchedulingCount={unscheduledJobs.length}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        onScrollToAttention={scrollToAttention}
      />

      <DispatchGoalPanel
        {...goalStats}
        revenueGoalPerStaff={REVENUE_GOAL_PER_STAFF}
        hoursPerStaffDay={HOURS_PER_STAFF_DAY}
      />

      <DispatchAttentionBar
        ref={attentionBarRef}
        unscheduledJobs={unscheduledJobs}
        availableTeamMembers={availableTeamMembers}
        showOpenSlots={showOpenSlots}
        setShowOpenSlots={setShowOpenSlots}
        unassignedJobsCount={unassignedJobs.length}
        timeOffToday={timeOffToday}
        pendingTimeOff={pendingTimeOff}
        getEmployeeById={getEmployeeById}
        onScheduleUnscheduled={(job) => {
          setNewJobData(prev => ({
            ...prev,
            clientId: job.clientId,
            clientName: job.clientName,
            address: job.address,
            price: job.price,
            notes: job.notes || '',
            existingJobId: job.id
          }));
          setShowAddJobModal(true);
        }}
        onQuickAdd={openAddJobModal}
        onOpenTimeOff={() => setShowTimeOffManager(true)}
      />

      <DayNotesPanel selectedDate={selectedDate} />

      <AutoGroupModal
        isOpen={showAutoGroupModal}
        onClose={() => setShowAutoGroupModal(false)}
        selectedDate={selectedDate}
        unassignedJobs={unassignedJobs}
        allJobsForDate={todayJobs}
        employees={state.employees}
        timeOffRequests={state.timeOffRequests}
        onApply={async (routes) => {
          // Apply each route: update every job with team + routeId in parallel
          const updates = [];
          for (const r of routes) {
            for (const job of r.jobs) {
              updates.push(updateJob(job.id, {
                team: r.teamIds,
                teamNames: r.teamNames,
                routeId: r.routeId,
                routeName: r.routeName,
              }));
            }
          }
          await Promise.all(updates);
          const totalJobs = routes.reduce((s, r) => s + r.jobs.length, 0);
          toast.success(`Assigned ${totalJobs} job${totalJobs !== 1 ? 's' : ''} across ${routes.length} route${routes.length !== 1 ? 's' : ''}.`);
        }}
      />

      {showOptimizePanel && (
        <div className="optimize-panel">
          <div className="optimize-header"><div className="optimize-title"><Lightbulb size={22} /><div><h3>Optimization Suggestions</h3><p>{optimizationSuggestions.length} suggestion{optimizationSuggestions.length !== 1 ? 's' : ''} found</p></div></div><button className="btn-icon" onClick={() => setShowOptimizePanel(false)}><X size={20} /></button></div>
          <div className="suggestions-list">
            {optimizationSuggestions.length === 0 ? <div className="no-suggestions"><Check size={32} /><p>Routes are already optimized!</p></div> : optimizationSuggestions.map((s, i) => (
              <div key={i} className={`suggestion-card ${s.type}`}>
                {s.type === 'assign' && (<><div className="suggestion-icon assign"><UserPlus size={18} /></div><div className="suggestion-content"><div className="suggestion-title">Assign "{s.job.clientName}" to {s.suggestedTeam.teamKey}</div><div className="suggestion-reason">{s.reason}</div><div className="suggestion-impact">{s.impact}</div></div><button className="btn btn-sm btn-primary" onClick={() => applyAssignSuggestion(s)}>Apply</button></>)}
                {s.type === 'swap' && (<><div className="suggestion-icon swap"><Shuffle size={18} /></div><div className="suggestion-content"><div className="suggestion-title">Move "{s.job.clientName}"</div><div className="suggestion-swap"><span className="from-team">{s.fromTeam.teamKey}</span><ArrowRight size={16} /><span className="to-team">{s.toTeam.teamKey}</span></div><div className="suggestion-reason">{s.reason}</div><div className="suggestion-impact savings">{s.impact}</div></div><button className="btn btn-sm btn-primary" onClick={() => applySwapSuggestion(s)}>Apply</button></>)}
                {s.type === 'add_team' && (<><div className="suggestion-icon add-team"><Users size={18} /></div><div className="suggestion-content"><div className="suggestion-title">Add another team</div><div className="suggestion-reason">{s.reason}</div><div className="suggestion-impact">{s.impact}</div></div><button className="btn btn-sm btn-secondary">Create Team</button></>)}
              </div>
            ))}
          </div>
          {optimizationSuggestions.length > 0 && <div className="optimize-footer"><button className="btn btn-primary" onClick={() => { optimizationSuggestions.forEach(s => { if (s.type === 'assign') applyAssignSuggestion(s); if (s.type === 'swap') applySwapSuggestion(s); }); }}>Apply All Suggestions</button></div>}
        </div>
      )}

      {view === 'map' && (
        <RouteMapView
          jobs={todayJobs}
          employees={state.employees}
          shifts={state.shifts}
          allJobs={state.jobs}
          selectedDate={selectedDate}
          onJobClick={setSelectedJob}
          onUpdateJob={updateJob}
          onOpenReschedule={(job) => {
            setSelectedJob(job);
            setRescheduleData({
              date: job.date,
              scheduledTime: job.scheduledTime || '09:00',
              duration: job.duration || 2.5
            });
            setShowRescheduleModal(true);
          }}
          onAddJob={openAddJobModal}
          onCreateInvoice={handleCreateInvoice}
          onMakeRecurring={openMakeRecurringModal}
          onSendReminder={setShowReminderModal}
          onRunningBehind={(job) => {
            setRunningBehindDelay('15');
            setRunningBehindCustom('');
            setRunningBehindNote('');
            setShowRunningBehindModal(job);
          }}
          onAssignTeam={openAssignModal}
          onDeleteJob={deleteJob}
          onDeleteSeries={async (recurringScheduleId) => {
            try {
              const result = await deleteSeriesJobs(recurringScheduleId, { scope: 'all_scheduled' });
              toast.success(`Deleted ${result.deleted} scheduled jobs from the series.`);
            } catch (e) {
              console.error('Failed to delete series:', e);
              toast.error('Failed to delete series. Please try again.');
            }
          }}
          getJobInvoice={getJobInvoice}
          getTeamColor={getTeamColor}
          creatingInvoice={creatingInvoice}
          navigate={navigate}
          invoices={state.invoices}
          clients={state.clients}
          logDispatchActivity={logDispatchActivity}
          isLoaded={isLoaded}
          loadError={loadError}
          retry={retry}
        />
      )}

      {view === 'live' && (
        <div className="live-tracking-layout">
          <div className="live-sidebar">
            <div className="live-header">
              <div className="live-indicator"><Radio size={16} className="pulse" /><span>Live Tracking</span></div>
              <span className="team-count">{Object.keys(filteredLiveLocations).length} clocked in</span>
            </div>
            <div className="live-team-list">
              {Object.values(filteredLiveLocations).length === 0 ? (
                <div className="no-live-data">
                  <Wifi size={48} />
                  <p>No team members clocked in</p>
                  <span>Live locations will appear when team members clock in via the Team App</span>
                </div>
              ) : (
                Object.values(filteredLiveLocations).map(loc => {
                  const timeSince = loc.timestamp ? formatDistanceToNow(new Date(loc.timestamp), { addSuffix: true }) : 'Unknown';
                  const isStale = loc.timestamp && (Date.now() - new Date(loc.timestamp).getTime()) > 120000; // 2 min
                  return (
                    <div
                      key={loc.id}
                      className={`live-team-card ${selectedTeamMember === loc.id ? 'selected' : ''} ${isStale ? 'stale' : ''}`}
                      onClick={() => setSelectedTeamMember(selectedTeamMember === loc.id ? null : loc.id)}
                    >
                      <div className="live-avatar" style={{ background: teamColors[Object.keys(filteredLiveLocations).indexOf(loc.id) % teamColors.length] }}>
                        {loc.empName?.split(' ').map(n => n[0]).join('') || '??'}
                      </div>
                      <div className="live-info">
                        <span className="live-name">{loc.empName || loc.empEmail}</span>
                        <span className={`live-status ${loc.status}`}>
                          {loc.status === 'working' ? '🧹 Working' : loc.status === 'en_route' ? '🚗 En Route' : '✓ Available'}
                        </span>
                        {loc.currentJobClient && (
                          <span className="live-job">@ {loc.currentJobClient}</span>
                        )}
                      </div>
                      <div className="live-meta">
                        <span className={`last-update ${isStale ? 'stale' : ''}`}>{timeSince}</span>
                        {loc.batteryLevel && <span className="battery">🔋 {loc.batteryLevel}%</span>}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <div className="live-map-container">
            {loadError && <div className="map-error"><AlertCircle size={48} /><h3>Map Error</h3><p>{loadError.message || 'Failed to load Google Maps.'}</p>{retry && <button className="retry-map-btn" onClick={retry}>Retry</button>}</div>}
            {!isLoaded && !loadError && <div className="map-loading"><div className="spinner" /><p>Loading map...</p></div>}
            {isLoaded && !loadError && (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={selectedTeamMember && filteredLiveLocations[selectedTeamMember]
                  ? { lat: filteredLiveLocations[selectedTeamMember].lat, lng: filteredLiveLocations[selectedTeamMember].lng }
                  : defaultCenter
                }
                zoom={selectedTeamMember ? 15 : 11}
                options={mapOptions}
              >
                {/* Office Marker */}
                <Marker
                  position={{ lat: OFFICE.lat, lng: OFFICE.lng }}
                  icon={{
                    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><circle cx="20" cy="20" r="18" fill="#1a2634" stroke="white" stroke-width="3"/><text x="20" y="26" text-anchor="middle" fill="white" font-size="14" font-weight="bold">HQ</text></svg>`),
                    scaledSize: new window.google.maps.Size(40, 40)
                  }}
                  title={OFFICE.name}
                />
                {/* Scheduled Job Markers (semi-transparent on live view) */}
                {todayJobs.map(job => {
                  const coords = getJobCoords(job);
                  if (!coords) return null;
                  const isCompleted = job.status === 'completed';
                  const isInProgress = job.status === 'in_progress';
                  const fillColor = isCompleted ? '#16a34a' : isInProgress ? '#2563eb' : '#94a3b8';
                  const fillOpacity = isCompleted ? '0.4' : isInProgress ? '0.9' : '0.5';
                  return (
                    <Marker
                      key={`live-job-${job.id}`}
                      position={coords}
                      icon={{
                        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="${fillColor}" opacity="${fillOpacity}" stroke="white" stroke-width="1.5"/><text x="12" y="16" text-anchor="middle" fill="white" font-size="9" font-weight="bold">${isCompleted ? '✓' : isInProgress ? '►' : '•'}</text></svg>`),
                        scaledSize: new window.google.maps.Size(24, 24),
                        anchor: new window.google.maps.Point(12, 12)
                      }}
                      title={`${job.clientName} - ${formatJobTime(job.scheduledTime)} (${jobStatuses[job.status]?.label || 'Scheduled'})`}
                      onClick={() => setSelectedJob(job)}
                    />
                  );
                })}
                {/* Live Team Markers - only clocked-in employees */}
                {Object.values(filteredLiveLocations).map((loc, idx) => (
                  loc.lat && loc.lng && (
                    <Marker
                      key={loc.id}
                      position={{ lat: loc.lat, lng: loc.lng }}
                      icon={{
                        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
                            <circle cx="24" cy="24" r="20" fill="${teamColors[idx % teamColors.length]}" stroke="white" stroke-width="3"/>
                            <circle cx="24" cy="24" r="24" fill="${teamColors[idx % teamColors.length]}" opacity="0.3">
                              <animate attributeName="r" from="20" to="28" dur="1.5s" repeatCount="indefinite"/>
                              <animate attributeName="opacity" from="0.3" to="0" dur="1.5s" repeatCount="indefinite"/>
                            </circle>
                            <text x="24" y="30" text-anchor="middle" fill="white" font-size="14" font-weight="bold">${loc.empName?.split(' ').map(n => n[0]).join('') || '?'}</text>
                          </svg>
                        `),
                        scaledSize: new window.google.maps.Size(48, 48),
                        anchor: new window.google.maps.Point(24, 24)
                      }}
                      title={`${loc.empName || 'Team Member'} - ${loc.status}`}
                      onClick={() => setSelectedTeamMember(loc.id)}
                    />
                  )
                ))}
              </GoogleMap>
            )}
            <div className="live-legend">
              <h4><Radio size={14} /> Live Positions</h4>
              {Object.values(filteredLiveLocations).map((loc, idx) => (
                <div key={loc.id} className="legend-item" onClick={() => setSelectedTeamMember(loc.id)}>
                  <div className="legend-dot" style={{ background: teamColors[idx % teamColors.length] }} />
                  <span>{loc.empName || loc.empEmail}</span>
                </div>
              ))}
              <h4 style={{ marginTop: 10 }}><MapPin size={14} /> Jobs</h4>
              <div className="legend-item"><div className="legend-dot" style={{ background: '#2563eb' }} /><span>In Progress</span></div>
              <div className="legend-item"><div className="legend-dot" style={{ background: '#16a34a', opacity: 0.5 }} /><span>Completed</span></div>
              <div className="legend-item"><div className="legend-dot" style={{ background: '#94a3b8', opacity: 0.6 }} /><span>Scheduled</span></div>
            </div>
          </div>
        </div>
      )}

      {showAssignModal && jobToAssign && (
        <div className="assign-modal-wrapper">
          <div className="modal-overlay" onClick={() => setShowAssignModal(false)} />
          <div className="assign-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Team</h2>
              <button className="close-btn" onClick={() => setShowAssignModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-job-info">
              <div className="job-summary">
                <h3>{jobToAssign.clientName}</h3>
                <p>{jobToAssign.address}, {jobToAssign.city}</p>
                <div className="job-details-row">
                  <span><Clock size={14} /> {formatJobTime(jobToAssign.scheduledTime)}</span>
                  <span><Timer size={14} /> {formatDuration(jobToAssign.duration)}</span>
                  <span className="price">${jobToAssign.price}</span>
                </div>
              </div>
            </div>
            <div className="team-selection">
              <h4>Select Team Members</h4>
              <p className="selection-hint">Typically 2 team members per job</p>
              <div className="team-grid">
                {scheduledTeamMembers.map(emp => {
                  const isSelected = selectedTeamMembers.includes(emp.id);
                  const hoursWorking = hoursPerMember[emp.displayName] || 0;
                  return (
                    <div 
                      key={emp.id} 
                      className={`team-member-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleTeamMember(emp.id)}
                    >
                      <div className="member-avatar" style={{ background: emp.color }}>
                        {emp.firstName.charAt(0)}{emp.lastName.charAt(0)}
                      </div>
                      <div className="member-info">
                        <span className="member-name">{emp.displayName}</span>
                        <span className="member-hours">{hoursWorking > 0 ? `${hoursWorking}h today` : 'Available'}</span>
                      </div>
                      <div className="member-check">{isSelected && <Check size={18} />}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAssignModal(false)}>Cancel</button>
              <button 
                className="btn btn-primary" 
                onClick={saveTeamAssignment} 
                disabled={selectedTeamMembers.length === 0}
              >
                <Check size={18} />
                {selectedTeamMembers.length === 0 ? 'Select Team' : `Assign ${selectedTeamMembers.length} Member${selectedTeamMembers.length > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Sidebar removed — all job actions handled via RouteMapView modal */}


      {/* Add Job Modal */}
      {showAddJobModal && (
        <div className="assign-modal-wrapper">
          <div className="modal-overlay" onClick={() => setShowAddJobModal(false)} />
          <div className="assign-modal add-job-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Job</h2>
              <button className="close-btn" onClick={() => setShowAddJobModal(false)}><X size={20} /></button>
            </div>
            <div className="add-job-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Client Name *</label>
                  <input
                    type="text"
                    placeholder="Enter client name"
                    value={newJobData.clientName}
                    onChange={(e) => setNewJobData({...newJobData, clientName: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Service Type</label>
                  <select
                    value={newJobData.serviceType}
                    onChange={(e) => setNewJobData({...newJobData, serviceType: e.target.value})}
                  >
                    {Object.entries(serviceTypes).filter(([k]) => k !== 'recurring').map(([key, st]) => (
                      <option key={key} value={key}>{st.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Address *</label>
                <input
                  type="text"
                  placeholder="Street address"
                  value={newJobData.address}
                  onChange={(e) => setNewJobData({...newJobData, address: e.target.value})}
                />
              </div>

              <div className="form-row three-col">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={newJobData.city}
                    onChange={(e) => setNewJobData({...newJobData, city: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={newJobData.state}
                    onChange={(e) => setNewJobData({...newJobData, state: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Zip</label>
                  <input
                    type="text"
                    value={newJobData.zip}
                    onChange={(e) => setNewJobData({...newJobData, zip: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    value={newJobData.date}
                    onChange={(e) => setNewJobData({...newJobData, date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Start Time</label>
                  <input
                    type="time"
                    value={newJobData.scheduledTime}
                    onChange={(e) => setNewJobData({...newJobData, scheduledTime: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Duration (hours)</label>
                  <select
                    value={newJobData.duration}
                    onChange={(e) => setNewJobData({...newJobData, duration: parseFloat(e.target.value)})}
                  >
                    <option value="1">1 hour</option>
                    <option value="1.5">1.5 hours</option>
                    <option value="2">2 hours</option>
                    <option value="2.5">2.5 hours</option>
                    <option value="3">3 hours</option>
                    <option value="3.5">3.5 hours</option>
                    <option value="4">4 hours</option>
                    <option value="4.5">4.5 hours</option>
                    <option value="5">5 hours</option>
                    <option value="6">6 hours</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Price ($)</label>
                  <input
                    type="number"
                    step="5"
                    value={newJobData.price}
                    onChange={(e) => setNewJobData({...newJobData, price: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  placeholder="Any special instructions..."
                  value={newJobData.notes}
                  onChange={(e) => setNewJobData({...newJobData, notes: e.target.value})}
                  rows={2}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddJobModal(false)}>Cancel</button>
              <button 
                className="btn btn-primary" 
                onClick={handleAddJob}
                disabled={!newJobData.clientName || !newJobData.address}
              >
                <Plus size={18} /> Add Job
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && selectedJob && (
        <div className="assign-modal-wrapper">
          <div className="modal-overlay" onClick={() => setShowRescheduleModal(false)} />
          <div className="assign-modal reschedule-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reschedule Job</h2>
              <button className="close-btn" onClick={() => setShowRescheduleModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="reschedule-job-info">
                <strong>{selectedJob.clientName}</strong>
                <span>{selectedJob.address}</span>
              </div>
              
              <div className="form-group">
                <label>New Date</label>
                <input
                  type="date"
                  value={rescheduleData.date}
                  onChange={(e) => setRescheduleData({...rescheduleData, date: e.target.value})}
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Start Time</label>
                  <input
                    type="time"
                    value={rescheduleData.scheduledTime}
                    onChange={(e) => setRescheduleData({...rescheduleData, scheduledTime: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Duration</label>
                  <select
                    value={rescheduleData.duration}
                    onChange={(e) => setRescheduleData({...rescheduleData, duration: parseFloat(e.target.value)})}
                  >
                    <option value="1">1 hour</option>
                    <option value="1.5">1.5 hours</option>
                    <option value="2">2 hours</option>
                    <option value="2.5">2.5 hours</option>
                    <option value="3">3 hours</option>
                    <option value="3.5">3.5 hours</option>
                    <option value="4">4 hours</option>
                    <option value="4.5">4.5 hours</option>
                    <option value="5">5 hours</option>
                    <option value="6">6 hours</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowRescheduleModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleReschedule}>
                <Calendar size={18} /> Reschedule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recurring Job Modal */}
      <RecurringJobModal
        isOpen={showRecurringModal}
        onClose={() => {
          setShowRecurringModal(false);
          setEditingJobForRecurring(null);
        }}
        onSave={handleRecurringModalSave}
        existingJob={editingJobForRecurring}
        initialDate={selectedDate}
        clients={state.clients}
        employees={state.employees}
        properties={state.properties}
      />

      {/* Duplicate Client Warning Modal */}
      {showDuplicateWarning && (
        <div className="modal-overlay" onClick={() => {
          setShowDuplicateWarning(false);
          setPendingJobData(null);
          setDuplicateClients([]);
        }}>
          <div className="modal-content duplicate-warning-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Possible Duplicate Client</h2>
              <button className="close-btn" onClick={() => {
                setShowDuplicateWarning(false);
                setPendingJobData(null);
                setDuplicateClients([]);
              }}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="warning-header">
                <AlertTriangle size={24} className="warning-icon" />
                <p>We found existing client(s) that may be the same person:</p>
              </div>
              
              <div className="duplicate-clients-list">
                {duplicateClients.map(client => (
                  <div key={client.id} className="duplicate-client-card">
                    <div className="client-info">
                      <strong>{client.firstName} {client.lastName}</strong>
                      {client.email && <span className="client-detail">{client.email}</span>}
                      {client.phone && <span className="client-detail">{client.phone}</span>}
                    </div>
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => handleUseExistingClientForJob(client)}
                    >
                      Use This Client
                    </button>
                  </div>
                ))}
              </div>

              <div className="warning-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDuplicateWarning(false);
                    setPendingJobData(null);
                    setDuplicateClients([]);
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-warning"
                  onClick={handleCreateClientAnyway}
                >
                  Create New Anyway
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Batch Send Reminders Modal */}
      {showBatchReminderModal && (() => {
        const eligibleJobs = todayJobs.filter(j => j.status !== 'cancelled' && j.status !== 'completed');
        const selectedCount = Object.values(batchSelectedJobs).filter(Boolean).length;
        const allSelected = eligibleJobs.length > 0 && eligibleJobs.every(j => batchSelectedJobs[j.id]);

        return (
          <div className="assign-modal-wrapper">
            <div className="modal-overlay" onClick={() => !batchSending && setShowBatchReminderModal(false)} />
            <div className="assign-modal batch-reminder-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header batch-reminder-header">
                <h2><Mail size={20} /> Send Appointment Reminders</h2>
                <button className="close-btn" onClick={() => !batchSending && setShowBatchReminderModal(false)} disabled={batchSending}><X size={20} /></button>
              </div>
              <div className="batch-reminder-body">
                <p className="batch-reminder-date">
                  <Calendar size={16} /> {format(parseISO(selectedDate), 'EEEE, MMMM d, yyyy')}
                </p>

                {batchResults ? (
                  /* Results View */
                  <div className="batch-results">
                    <div className="batch-results-summary">
                      {batchProgress.sent > 0 && (
                        <div className="batch-result-stat sent">
                          <CheckCircle size={18} />
                          <span>{batchProgress.sent} sent</span>
                        </div>
                      )}
                      {batchProgress.failed > 0 && (
                        <div className="batch-result-stat failed">
                          <AlertCircle size={18} />
                          <span>{batchProgress.failed} failed</span>
                        </div>
                      )}
                      {batchProgress.skipped > 0 && (
                        <div className="batch-result-stat skipped">
                          <AlertTriangle size={18} />
                          <span>{batchProgress.skipped} skipped</span>
                        </div>
                      )}
                    </div>
                    <div className="batch-results-list">
                      {batchResults.map(r => (
                        <div key={r.jobId} className={`batch-result-row ${r.status}`}>
                          <span className="batch-result-icon">
                            {r.status === 'sent' && <CheckCircle size={14} />}
                            {r.status === 'failed' && <AlertCircle size={14} />}
                            {r.status === 'skipped' && <AlertTriangle size={14} />}
                          </span>
                          <span className="batch-result-name">{r.clientName || 'Unknown'}</span>
                          <span className="batch-result-detail">
                            {r.status === 'sent' && (
                              <>
                                {r.emailSent && <span title="Email sent"><Mail size={12} /> {r.email}</span>}
                                {r.emailSent && r.smsSent && <span className="batch-job-sep"> &middot; </span>}
                                {r.smsSent && <span title="SMS sent"><MessageSquare size={12} /> SMS</span>}
                              </>
                            )}
                            {r.status === 'failed' && (r.error || 'Send failed')}
                            {r.status === 'skipped' && (r.reason || 'Skipped')}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="batch-reminder-actions">
                      <button className="btn btn-primary" onClick={() => setShowBatchReminderModal(false)}>Done</button>
                    </div>
                  </div>
                ) : batchSending ? (
                  /* Sending Progress View */
                  <div className="batch-sending-progress">
                    <Loader size={32} className="spin" />
                    <p className="batch-progress-text">
                      Sending reminders... {batchProgress.sent + batchProgress.failed + batchProgress.skipped} / {batchProgress.total}
                    </p>
                    <div className="batch-progress-bar">
                      <div
                        className="batch-progress-fill"
                        style={{ width: `${((batchProgress.sent + batchProgress.failed + batchProgress.skipped) / batchProgress.total) * 100}%` }}
                      />
                    </div>
                    {batchProgress.sent > 0 && <span className="batch-progress-sent">{batchProgress.sent} sent</span>}
                  </div>
                ) : (
                  /* Selection View */
                  <>
                    <div className="batch-select-header">
                      <label className="batch-select-all">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={() => {
                            const next = {};
                            eligibleJobs.forEach(j => { next[j.id] = !allSelected; });
                            setBatchSelectedJobs(next);
                          }}
                        />
                        <span>Select All ({eligibleJobs.length} jobs)</span>
                      </label>
                      <div className="batch-quick-select">
                        <button
                          type="button"
                          className="batch-quick-btn"
                          onClick={() => {
                            const next = {};
                            eligibleJobs.forEach(j => {
                              const client = state.clients.find(c => c.id === j.clientId);
                              const hasContact = !!(client?.email || client?.phone);
                              if (hasContact && !j.reminderSent) next[j.id] = true;
                            });
                            setBatchSelectedJobs(next);
                          }}
                        >
                          Unsent only
                        </button>
                        <button
                          type="button"
                          className="batch-quick-btn"
                          disabled={selectedCount === 0}
                          onClick={() => setBatchSelectedJobs({})}
                        >
                          Clear
                        </button>
                      </div>
                      <span className="batch-selected-count">{selectedCount} selected</span>
                    </div>
                    <div className="batch-job-list">
                      {eligibleJobs.length === 0 ? (
                        <div className="batch-empty">No active jobs for this date.</div>
                      ) : eligibleJobs.map(job => {
                        const client = state.clients.find(c => c.id === job.clientId);
                        const hasEmail = !!client?.email;
                        const hasPhone = !!client?.phone;
                        const hasContact = hasEmail || hasPhone;

                        return (
                          <label key={job.id} className={`batch-job-row ${!hasContact ? 'no-email' : ''} ${job.reminderSent ? 'already-sent' : ''}`}>
                            <input
                              type="checkbox"
                              checked={!!batchSelectedJobs[job.id]}
                              disabled={!hasContact}
                              onChange={() => setBatchSelectedJobs(prev => ({ ...prev, [job.id]: !prev[job.id] }))}
                            />
                            <div className="batch-job-info">
                              <span className="batch-job-client">{job.clientName}</span>
                              <span className="batch-job-details">
                                <Clock size={12} /> {formatJobTime(job.scheduledTime)}
                                <span className="batch-job-sep">&middot;</span>
                                <MapPin size={12} /> {job.address}
                              </span>
                              {!hasContact && <span className="batch-job-warning">No email or phone on file</span>}
                              <span className="batch-job-channels">
                                {hasEmail && <span className="batch-job-email"><Mail size={11} /> {client.email}</span>}
                                {hasPhone && <span className="batch-job-phone"><MessageSquare size={11} /> {client.phone}</span>}
                              </span>
                            </div>
                            <div className="batch-job-status">
                              {job.reminderSent && (
                                <span className="batch-already-sent" title={`Sent ${job.reminderSentAt ? format(new Date(job.reminderSentAt), 'MMM d, h:mm a') : ''}`}>
                                  <CheckCircle size={14} /> Sent
                                </span>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    <div className="batch-reminder-actions">
                      <button className="btn btn-secondary" onClick={() => setShowBatchReminderModal(false)}>Cancel</button>
                      <button
                        className="btn btn-primary"
                        disabled={selectedCount === 0}
                        onClick={handleBatchSendReminders}
                      >
                        <Send size={16} /> Send {selectedCount} Reminder{selectedCount !== 1 ? 's' : ''}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Send Reminder Modal */}
      {showReminderModal && (() => {
        const job = showReminderModal;
        const client = state.clients.find(c => c.id === job.clientId);
        
        return (
          <div className="assign-modal-wrapper">
            <div className="modal-overlay" onClick={() => setShowReminderModal(null)} />
            <div className="assign-modal reminder-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Send Appointment Reminder</h2>
                <button className="close-btn" onClick={() => setShowReminderModal(null)}><X size={20} /></button>
              </div>
              <div className="reminder-modal-body">
                <div className="reminder-job-info">
                  <h3>{job.clientName}</h3>
                  <p><Calendar size={16} /> {format(parseISO(job.date), 'EEEE, MMMM d, yyyy')}</p>
                  <p><Clock size={16} /> {formatJobTime(job.scheduledTime)}</p>
                  <p><MapPin size={16} /> {job.address}</p>
                </div>

                <div className="reminder-options">
                  <h4>Send via:</h4>
                  
                  {/* Email Option */}
                  <button 
                    className="reminder-option-btn"
                    disabled={!client?.email || sendingReminder === 'email'}
                    onClick={async () => {
                      setSendingReminder('email');
                      try {
                        const emailLogId = `log_${Date.now()}`;
                        await sendAppointmentReminder({
                          clientId: client.id,
                          commLogId: emailLogId,
                          clientEmail: client.email,
                          clientName: job.clientName,
                          date: format(parseISO(job.date), 'EEEE, MMMM d, yyyy'),
                          time: formatJobTime(job.scheduledTime),
                          address: `${job.address}, ${job.city || 'Charlottesville'}, ${job.state || 'VA'}`
                        });

                        await logClientCommunication(client.id, {
                          id: emailLogId,
                          type: 'email',
                          category: 'appointment_reminder',
                          subject: 'Appointment Reminder',
                          recipient: client.email,
                          status: 'sent',
                          details: { jobId: job.id, jobDate: job.date }
                        });
                        
                        await updateJob(job.id, { 
                          reminderSent: true, 
                          reminderSentAt: new Date().toISOString(),
                          reminderMethod: 'email'
                        });
                        
                        toast.success('Email reminder sent!');
                        setShowReminderModal(null);
                      } catch (error) {
                        console.error('Failed to send email:', error);
                        toast.error('Failed to send email reminder.');
                      } finally {
                        setSendingReminder(null);
                      }
                    }}
                  >
                    <div className="option-icon email"><Mail size={24} /></div>
                    <div className="option-info">
                      <span className="option-title">Email</span>
                      <span className="option-detail">{client?.email || 'No email on file'}</span>
                    </div>
                    {sendingReminder === 'email' && <Loader size={18} className="spin" />}
                  </button>

                  {/* Text Option */}
                  <button 
                    className="reminder-option-btn"
                    disabled={!client?.phone || sendingReminder === 'text'}
                    onClick={async () => {
                      setSendingReminder('text');
                      try {
                        await sendSMS('appointment_reminder', {
                          phone: client.phone,
                          clientName: job.clientName,
                          date: format(parseISO(job.date), 'EEEE, MMMM d'),
                          time: formatJobTime(job.scheduledTime),
                          portalLink: 'https://keelgroup.app/portal'
                        });
                        
                        await logClientCommunication(client.id, {
                          type: 'sms',
                          category: 'appointment_reminder',
                          subject: 'Appointment Reminder',
                          recipient: client.phone,
                          status: 'sent',
                          details: { jobId: job.id, jobDate: job.date }
                        });
                        
                        await updateJob(job.id, { 
                          reminderSent: true, 
                          reminderSentAt: new Date().toISOString(),
                          reminderMethod: 'text'
                        });
                        
                        toast.success('Text reminder sent!');
                        setShowReminderModal(null);
                      } catch (error) {
                        console.error('Failed to send text:', error);
                        toast.error('Failed to send text reminder.');
                      } finally {
                        setSendingReminder(null);
                      }
                    }}
                  >
                    <div className="option-icon text"><MessageSquare size={24} /></div>
                    <div className="option-info">
                      <span className="option-title">Text Message</span>
                      <span className="option-detail">{client?.phone || 'No phone on file'}</span>
                    </div>
                    {sendingReminder === 'text' && <Loader size={18} className="spin" />}
                  </button>

                  {/* Both Option */}
                  <button 
                    className="reminder-option-btn both"
                    disabled={(!client?.email && !client?.phone) || sendingReminder === 'both'}
                    onClick={async () => {
                      setSendingReminder('both');
                      let emailSent = false;
                      let textSent = false;
                      
                      try {
                        // Send email if available
                        if (client?.email) {
                          try {
                            const bothLogId = `log_${Date.now()}`;
                            await sendAppointmentReminder({
                              clientId: client.id,
                              commLogId: bothLogId,
                              clientEmail: client.email,
                              clientName: job.clientName,
                              date: format(parseISO(job.date), 'EEEE, MMMM d, yyyy'),
                              time: formatJobTime(job.scheduledTime),
                              address: `${job.address}, ${job.city || 'Charlottesville'}, ${job.state || 'VA'}`
                            });
                            emailSent = true;

                            await logClientCommunication(client.id, {
                              id: bothLogId,
                              type: 'email',
                              category: 'appointment_reminder',
                              subject: 'Appointment Reminder',
                              recipient: client.email,
                              status: 'sent',
                              details: { jobId: job.id, jobDate: job.date }
                            });
                          } catch (e) {
                            console.error('Email failed:', e);
                          }
                        }
                        
                        // Send text if available
                        if (client?.phone) {
                          try {
                            await sendSMS('appointment_reminder', {
                              phone: client.phone,
                              clientName: job.clientName,
                              date: format(parseISO(job.date), 'EEEE, MMMM d'),
                              time: formatJobTime(job.scheduledTime),
                              portalLink: 'https://keelgroup.app/portal'
                            });
                            textSent = true;
                            
                            await logClientCommunication(client.id, {
                              type: 'sms',
                              category: 'appointment_reminder',
                              subject: 'Appointment Reminder',
                              recipient: client.phone,
                              status: 'sent',
                              details: { jobId: job.id, jobDate: job.date }
                            });
                          } catch (e) {
                            console.error('Text failed:', e);
                          }
                        }
                        
                        await updateJob(job.id, { 
                          reminderSent: true, 
                          reminderSentAt: new Date().toISOString(),
                          reminderMethod: 'both'
                        });
                        
                        if (emailSent && textSent) {
                          toast.success('Email and text reminders sent!');
                        } else if (emailSent) {
                          toast.warning('Email sent! Text failed.');
                        } else if (textSent) {
                          toast.warning('Text sent! Email failed.');
                        } else {
                          toast.error('Failed to send reminders.');
                        }
                        setShowReminderModal(null);
                      } catch (error) {
                        console.error('Failed to send reminders:', error);
                        toast.error('Failed to send reminders.');
                      } finally {
                        setSendingReminder(null);
                      }
                    }}
                  >
                    <div className="option-icon both"><Send size={24} /></div>
                    <div className="option-info">
                      <span className="option-title">Both Email & Text</span>
                      <span className="option-detail">Send via all available channels</span>
                    </div>
                    {sendingReminder === 'both' && <Loader size={18} className="spin" />}
                  </button>
                </div>

                {job.reminderSent && (
                  <p className="reminder-sent-note">
                    <CheckCircle size={14} /> Reminder previously sent {job.reminderSentAt ? format(new Date(job.reminderSentAt), 'MMM d, h:mm a') : ''}
                    {job.reminderMethod && ` via ${job.reminderMethod}`}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })()}
        </>
      )}
      {/* Time Off Manager Modal */}

      {/* Running Behind Modal */}
      {showRunningBehindModal && (() => {
        const job = showRunningBehindModal;
        const client = state.clients.find(c => c.id === job.clientId);
        const delayMinutes = runningBehindDelay === 'custom' ? parseInt(runningBehindCustom) || 0 : parseInt(runningBehindDelay);
        
        // Calculate new ETA
        const getNewEta = () => {
          if (!job.scheduledTime || !delayMinutes) return null;
          const [h, m] = job.scheduledTime.split(':').map(Number);
          const totalMin = h * 60 + m + delayMinutes;
          const newH = Math.floor(totalMin / 60) % 24;
          const newM = totalMin % 60;
          const period = newH >= 12 ? 'PM' : 'AM';
          const displayH = newH > 12 ? newH - 12 : newH === 0 ? 12 : newH;
          return `${displayH}:${String(newM).padStart(2, '0')} ${period}`;
        };

        const handleSendRunningBehind = async (channel) => {
          if (!client) { toast.error('Client not found'); return; }
          if (!delayMinutes || delayMinutes <= 0) { toast.warning('Please select a delay time'); return; }
          
          setSendingRunningBehind(channel);
          const newEta = getNewEta() || 'shortly';
          let emailSent = false;
          let textSent = false;

          try {
            if ((channel === 'email' || channel === 'both') && client.email) {
              try {
                const behindLogId = `log_${Date.now()}`;
                await sendAppointmentReminder({
                  clientId: client.id,
                  commLogId: behindLogId,
                  clientEmail: client.email,
                  clientName: job.clientName,
                  date: format(parseISO(job.date), 'EEEE, MMMM d, yyyy'),
                  time: `Our team is running approximately ${delayMinutes} minutes behind schedule. New estimated arrival: ${newEta}.${runningBehindNote ? ` Note: ${runningBehindNote}` : ''} We apologize for any inconvenience!`,
                  address: `${job.address}, ${job.city || 'Charlottesville'}, ${job.state || 'VA'}`
                });
                emailSent = true;
                await logClientCommunication(client.id, {
                  id: behindLogId,
                  type: 'email',
                  category: 'running_behind',
                  subject: `Running Behind - ${delayMinutes} min delay`,
                  recipient: client.email,
                  status: 'sent',
                  details: { jobId: job.id, jobDate: job.date, delay: delayMinutes, newEta }
                });
              } catch (e) { console.error('Email failed:', e); }
            }

            if ((channel === 'text' || channel === 'both') && client.phone) {
              try {
                await sendSMS('team_running_late', {
                  phone: client.phone,
                  delay: `${delayMinutes} minutes`,
                  eta: newEta
                });
                textSent = true;
                await logClientCommunication(client.id, {
                  type: 'sms',
                  category: 'running_behind',
                  subject: `Running Behind - ${delayMinutes} min delay`,
                  recipient: client.phone,
                  status: 'sent',
                  details: { jobId: job.id, jobDate: job.date, delay: delayMinutes, newEta }
                });
              } catch (e) { console.error('Text failed:', e); }
            }

            await updateJob(job.id, {
              runningBehind: true,
              runningBehindDelay: delayMinutes,
              runningBehindNewEta: newEta,
              runningBehindNotifiedAt: new Date().toISOString(),
              runningBehindMethod: channel,
              runningBehindNote: runningBehindNote || null
            });

            logDispatchActivity({
              action: 'running_behind_notification',
              description: `Notified ${job.clientName} - running ${delayMinutes} min behind (new ETA: ${newEta})`,
              jobDate: job.date,
              details: { client: job.clientName, delay: delayMinutes, newEta, channel }
            });

            if (channel === 'both') {
              if (emailSent && textSent) toast.success('Client notified via email & text!');
              else if (emailSent) toast.warning('Email sent! Text failed.');
              else if (textSent) toast.warning('Text sent! Email failed.');
              else toast.error('Failed to send notifications.');
            } else if ((channel === 'email' && emailSent) || (channel === 'text' && textSent)) {
              toast.success(`Client notified via ${channel}!`);
            } else {
              toast.error(`Failed to send ${channel} notification.`);
            }

            setShowRunningBehindModal(null);
          } catch (error) {
            console.error('Running behind notification error:', error);
            toast.error('Failed to send notification. Please try again.');
          } finally {
            setSendingRunningBehind(null);
          }
        };

        return (
          <div className="assign-modal-wrapper">
            <div className="modal-overlay" onClick={() => setShowRunningBehindModal(null)} />
            <div className="assign-modal running-behind-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header running-behind-header">
                <h2><AlertTriangle size={20} /> Running Behind</h2>
                <button className="close-btn" onClick={() => setShowRunningBehindModal(null)}><X size={20} /></button>
              </div>
              <div className="running-behind-modal-body">
                <div className="reminder-job-info">
                  <h3>{job.clientName}</h3>
                  <p><Calendar size={16} /> {format(parseISO(job.date), 'EEEE, MMMM d, yyyy')}</p>
                  <p><Clock size={16} /> Scheduled: {formatJobTime(job.scheduledTime)}</p>
                  <p><MapPin size={16} /> {job.address}</p>
                </div>

                <div className="delay-selection">
                  <h4>How far behind?</h4>
                  <div className="delay-options">
                    {['15', '30', '45', '60'].map(mins => (
                      <button
                        key={mins}
                        className={`delay-chip ${runningBehindDelay === mins ? 'active' : ''}`}
                        onClick={() => { setRunningBehindDelay(mins); setRunningBehindCustom(''); }}
                      >
                        {mins} min
                      </button>
                    ))}
                    <button
                      className={`delay-chip ${runningBehindDelay === 'custom' ? 'active' : ''}`}
                      onClick={() => setRunningBehindDelay('custom')}
                    >
                      Custom
                    </button>
                  </div>
                  {runningBehindDelay === 'custom' && (
                    <div className="custom-delay-input">
                      <input
                        type="number"
                        min="5"
                        max="180"
                        placeholder="Minutes"
                        value={runningBehindCustom}
                        onChange={e => setRunningBehindCustom(e.target.value)}
                        autoFocus
                      />
                      <span>minutes</span>
                    </div>
                  )}
                </div>

                {delayMinutes > 0 && getNewEta() && (
                  <div className="new-eta-preview">
                    <Clock size={16} />
                    <span>New ETA: <strong>{getNewEta()}</strong></span>
                  </div>
                )}

                <div className="running-behind-note">
                  <h4>Add a note <span>(optional)</span></h4>
                  <input
                    type="text"
                    placeholder="e.g., Previous job took longer than expected"
                    value={runningBehindNote}
                    onChange={e => setRunningBehindNote(e.target.value)}
                  />
                </div>

                <div className="reminder-options">
                  <h4>Notify client via:</h4>

                  <button
                    className="reminder-option-btn"
                    disabled={!client?.email || sendingRunningBehind === 'email'}
                    onClick={() => handleSendRunningBehind('email')}
                  >
                    <div className="option-icon email"><Mail size={24} /></div>
                    <div className="option-info">
                      <span className="option-title">Email</span>
                      <span className="option-detail">{client?.email || 'No email on file'}</span>
                    </div>
                    {sendingRunningBehind === 'email' && <Loader size={18} className="spin" />}
                  </button>

                  <button
                    className="reminder-option-btn"
                    disabled={!client?.phone || sendingRunningBehind === 'text'}
                    onClick={() => handleSendRunningBehind('text')}
                  >
                    <div className="option-icon text"><MessageSquare size={24} /></div>
                    <div className="option-info">
                      <span className="option-title">Text Message</span>
                      <span className="option-detail">{client?.phone || 'No phone on file'}</span>
                    </div>
                    {sendingRunningBehind === 'text' && <Loader size={18} className="spin" />}
                  </button>

                  <button
                    className="reminder-option-btn both"
                    disabled={(!client?.email && !client?.phone) || sendingRunningBehind === 'both'}
                    onClick={() => handleSendRunningBehind('both')}
                  >
                    <div className="option-icon both"><Send size={24} /></div>
                    <div className="option-info">
                      <span className="option-title">Both Email & Text</span>
                      <span className="option-detail">Send via all available channels</span>
                    </div>
                    {sendingRunningBehind === 'both' && <Loader size={18} className="spin" />}
                  </button>
                </div>

                {job.runningBehind && (
                  <p className="reminder-sent-note">
                    <AlertTriangle size={14} /> Previously notified {job.runningBehindNotifiedAt ? format(new Date(job.runningBehindNotifiedAt), 'MMM d, h:mm a') : ''}
                    {job.runningBehindDelay && ` (${job.runningBehindDelay} min delay)`}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      <TimeOffManager
        isOpen={showTimeOffManager}
        onClose={() => setShowTimeOffManager(false)}
      />

      {/* Appointment Confirmation Prompt */}
      {showConfirmationPrompt && confirmationJobData && (
        <div className="assign-modal-wrapper">
          <div className="modal-overlay" onClick={() => { setShowConfirmationPrompt(false); setConfirmationJobData(null); }} />
          <div className="assign-modal confirmation-prompt-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Job Saved! Send Confirmation?</h2>
              <button className="close-btn" onClick={() => { setShowConfirmationPrompt(false); setConfirmationJobData(null); }}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="confirmation-job-summary">
                <p><strong>{confirmationJobData.clientName}</strong></p>
                <p><Calendar size={14} style={{display:'inline', verticalAlign:'middle'}} /> {format(parseISO(confirmationJobData.date), 'EEEE, MMMM d, yyyy')}</p>
                <p><Clock size={14} style={{display:'inline', verticalAlign:'middle'}} /> {formatJobTime(confirmationJobData.scheduledTime)}</p>
                <p><MapPin size={14} style={{display:'inline', verticalAlign:'middle'}} /> {confirmationJobData.address}</p>
              </div>
              
              {(() => {
                const client = confirmationJobData.client || state.clients.find(c => c.id === confirmationJobData.clientId);
                const hasPhone = client?.phone;
                const hasEmail = client?.email;
                const hasContact = hasPhone || hasEmail;
                
                if (!hasContact) {
                  return (
                    <div className="no-contact-warning">
                      <AlertCircle size={16} />
                      <span>No phone or email on file for this client. Confirmation can't be sent.</span>
                    </div>
                  );
                }
                
                return (
                  <div className="confirmation-send-options">
                    <p className="send-label">Send appointment confirmation via:</p>
                    {hasPhone && (
                      <button 
                        className="confirmation-option-btn text-option"
                        onClick={() => handleSendAppointmentConfirmation('text')}
                        disabled={sendingConfirmation}
                      >
                        <MessageSquare size={20} />
                        <div>
                          <span className="option-title">Text Message</span>
                          <span className="option-detail">{client.phone}</span>
                        </div>
                        {sendingConfirmation && <Loader size={16} className="spinner" />}
                      </button>
                    )}
                    {hasEmail && (
                      <button 
                        className="confirmation-option-btn email-option"
                        onClick={() => handleSendAppointmentConfirmation('email')}
                        disabled={sendingConfirmation}
                      >
                        <Mail size={20} />
                        <div>
                          <span className="option-title">Email</span>
                          <span className="option-detail">{client.email}</span>
                        </div>
                        {sendingConfirmation && <Loader size={16} className="spinner" />}
                      </button>
                    )}
                    {hasPhone && hasEmail && (
                      <button 
                        className="confirmation-option-btn both-option"
                        onClick={() => handleSendAppointmentConfirmation('both')}
                        disabled={sendingConfirmation}
                      >
                        <Send size={20} />
                        <div>
                          <span className="option-title">Both Text & Email</span>
                          <span className="option-detail">Send via all channels</span>
                        </div>
                        {sendingConfirmation && <Loader size={16} className="spinner" />}
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => { setShowConfirmationPrompt(false); setConfirmationJobData(null); }}
              >
                Skip — Don't Send
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Dispatch Activity Log */}
      <DispatchLog
        isOpen={showDispatchLog}
        onClose={() => setShowDispatchLog(false)}
        selectedDate={selectedDate}
      />

      {/* Series Update Modal */}
      {showSeriesModal && (
        <div className="assign-modal-wrapper">
          <div className="modal-overlay" onClick={() => { setShowSeriesModal(null); setEditingJobField(null); }} />
          <div className="assign-modal series-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Update Series?</h2>
              <button className="close-btn" onClick={() => { setShowSeriesModal(null); setEditingJobField(null); }}><X size={20} /></button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <p style={{ marginBottom: 16, color: '#374151', lineHeight: 1.5 }}>
                {showSeriesModal.field === 'notes'
                  ? <>This client has upcoming appointments. Update notes for <strong>just this appointment</strong> or <strong>all future appointments</strong>?</>
                  : <>This job is part of a recurring series. Would you like to update <strong>just this appointment</strong> or <strong>this and all future appointments</strong>?</>
                }
              </p>
              <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: 20 }}>
                Changing: <strong>{showSeriesModal.field === 'notes' ? 'Job Notes' : 'Job Type'}</strong>
              </p>
              <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
                <button
                  className="btn btn-primary full-width"
                  onClick={() => handleSeriesUpdate('this')}
                  disabled={savingJobEdit}
                >
                  Just This Appointment
                </button>
                <button
                  className="btn btn-sage full-width"
                  onClick={() => handleSeriesUpdate('all')}
                  disabled={savingJobEdit}
                >
                  This & All Future Appointments
                </button>
                <button
                  className="btn btn-secondary full-width"
                  onClick={() => { setShowSeriesModal(null); setEditingJobField(null); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notify Client Modal */}
      {showNotifyClientModal && (
        <div className="assign-modal-wrapper">
          <div className="modal-overlay" onClick={() => setShowNotifyClientModal(null)} />
          <div className="assign-modal notify-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Notify Client</h2>
              <button className="close-btn" onClick={() => setShowNotifyClientModal(null)}><X size={20} /></button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <p style={{ marginBottom: 16, color: '#374151' }}>
                Send a notification to <strong>{showNotifyClientModal.clientName}</strong>:
              </p>
              <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                <button
                  className={`btn ${notifyType === 'booked' ? 'btn-primary' : 'btn-secondary'} full-width`}
                  onClick={() => setNotifyType('booked')}
                >
                  ✅ Job Booked
                </button>
                <button
                  className={`btn ${notifyType === 'rescheduled' ? 'btn-primary' : 'btn-secondary'} full-width`}
                  onClick={() => setNotifyType('rescheduled')}
                >
                  📅 Rescheduled
                </button>
              </div>
              <div style={{ background: '#f8f7f5', padding: 14, borderRadius: 8, fontSize: '0.82rem', color: '#374151', marginBottom: 16 }}>
                <p style={{ margin: '4px 0' }}><strong>Date:</strong> {format(parseISO(showNotifyClientModal.date), 'EEEE, MMMM d, yyyy')}</p>
                <p style={{ margin: '4px 0' }}><strong>Time:</strong> {formatJobTime(showNotifyClientModal.scheduledTime)}</p>
                <p style={{ margin: '4px 0' }}><strong>Service:</strong> {serviceTypes[showNotifyClientModal.serviceType]?.label || showNotifyClientModal.serviceType}</p>
              </div>
              <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 16 }}>
                An email will be sent to the client confirming their appointment details.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowNotifyClientModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleNotifyClient} disabled={sendingNotify}>
                {sendingNotify ? <><Loader size={16} className="spin" /> Sending...</> : <><Mail size={16} /> Send Notification</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <DispatchPrintView
        jobs={state.jobs}
        selectedDate={selectedDate}
        timeOffRequests={state.timeOffRequests}
        getEmployeeById={getEmployeeById}
      />
    </div>
  );
}

export default Dispatch;
